/* @moldura excecao — a Tela Cheia desta tela leva só a faixa + o calendário
   e usa esse elemento como portal dos modais, igual à agenda nova. Só o topo
   foi migrado. */
/* =============================================================================
   ⚠️ VERSÃO ANTIGA — CONGELADA EM 25/08/2026, GUARDADA PARA CONFERÊNCIA
   =============================================================================
   Esta é a tela como ela era ANTES da limpeza de 25/08. Ela **não é a tela
   oficial** — a oficial é a irmã sem o sufixo, e é ela que responde na rota de
   sempre. Esta responde em `-antiga` e existe por um motivo só, dado por ele:

     *"se lá na frente a gente detectar que teve alguma coisa que saiu de ordem,
     a gente pode consultar a antiga."*

   NÃO CORRIJA NADA AQUI. Se algo estiver errado nela, o conserto é na oficial —
   o valor deste arquivo é ser exatamente o que estava no ar, defeitos inclusos.
   Ele carrega o CSS numa string de template (`CSS_LOCAL`/`CSS_BARRA`) e as
   classes `card-barra-*` / `evento-barra-*`, que na oficial viraram
   `casaa-cartao-agendamento`.

   ⏳ PENDÊNCIA COMBINADA: apagar as duas telas `-antiga` (e suas rotas) quando o
   redesenho fechar e estiver tudo conferido. Está na ficha da conversa.
   ============================================================================= */
/**
 * agendamentos.tsx (agenda) — A MESA DE TRABALHO DA SUANY: é aqui que a
 * instalação é MARCADA. Nasceu como "Teste 1", virou a agenda oficial em
 * 09/08/2026 (a primeira tela foi removida) e ganhou este nome no mesmo dia,
 * quando a agenda dos instaladores passou a existir em separado.
 * Ver .claude/plans/ativo/agenda-agendamento-2026/00-plano.md.
 *
 * DUAS TELAS, E DE PROPOSITO (decisao dele, 09/08/2026):
 *   - `agendamentos.tsx` (esta) — quem marca. Tem "OS a agendar", arrastar,
 *     editar, excluir, imprimir roteiro. E de escritorio.
 *   - `instalacoes.tsx` — o que o instalador ve no celular: so o calendario da
 *     semana, sem OS a agendar e sem editar nada.
 * Nao e a mesma tela com permissoes diferentes: sao telas diferentes, porque o
 * trabalho e diferente.
 *
 * A empresa tera OUTRAS agendas (ferias, eventos da loja) — elas se distinguem
 * pelo `st_tipo` da visita, na mesma tabela, e nao por estrutura paralela.
 *
 * Desenho: faixa horizontal de "OS a agendar" EM CIMA (slider Swiper) +
 * calendário embaixo. Arrasta-se a OS pelo grip até um dia.
 *
 * Decisões que vêm da planilha real da Suany (7 meses lidos):
 *  - dayGrid, não timeGrid: hora é EXCEÇÃO na operação dela (a maioria das linhas
 *    não tem horário; só quando o cliente impõe). Grade de horas seria 90% vazia.
 *  - Semana Seg–Sex por padrão (a planilha dela não tem fim de semana).
 *  - **Búzios e Trancoso são agendas distintas e não se misturam** — o filtro de
 *    loja não é conveniência, é regra de leitura (Vittorio, 17/07).
 *  - Cor = TIPO de serviço. É o sinal principal do cartão; o ícone é só apoio.
 *
 * Swiper × arrastar: os dois disputam o mesmo gesto de ponteiro. Resolvido dando
 * ao dnd-kit APENAS o grip (setActivatorNodeRef) e marcando o grip com a classe
 * `no-swipe` (noSwipingClass) pro Swiper ignorar arrasto que começa ali.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Modal, Tab, Tabs } from 'react-bootstrap';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import ptBr from '@fullcalendar/core/locales/pt-br';
import type { MoreLinkAction } from '@fullcalendar/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { addDias, formatarDiaMes, hojeISO, inicioSemana } from '@/lib/data';
import { nomeCurtoDaViagem, roteiroCompleto } from '@/lib/viagem';
import type {
  CustoEvento,
  Instalador,
  OSAgendar,
  OSDetalhe,
  OSVisita,
  PrioridadeVisita,
  TipoEvento,
  TipoOS,
  VisitaAgenda,
} from '@/pages/ordens-servico/shared';
import {
  MOTIVOS_NAO_REALIZADA,
  PRIORIDADES,
  STATUS_VISITA,
  rotuloMotivo,
  rotuloStatus,
  rotuloStatusVisita,
  rotuloTipo,
} from '@/pages/ordens-servico/shared';
import { CustomCampoData, CustomCampoHora, DP_PORTAL_ID } from '@/components/CustomCamposData';
import { CustomAutoTextarea } from '@/components/CustomAutoTextarea';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

/**
 * Viagem operacional que cobre um intervalo de dias — o mínimo que o calendário
 * precisa pra marcar os dias com viagem e ligar/desligar um evento nela (Fatia 4).
 */
type ViagemLite = {
  id: number;
  nome_curto: string | null;
  destinos: string[];
  data_partida: string | null;
  data_chegada: string | null;
  loja_id: number | null;
  status: string;
};

/**
 * Rótulo de uma viagem ONDE O ESPAÇO É POUCO (pílula do dia, chip do card):
 * o nome curto que a Suany deu, ou a sugestão calculada do roteiro.
 *
 * Antes isto era `destinos.join(' → ')`, e com duas paradas já saía
 * "Rio de Janeiro → Niterói", que tomava o cabeçalho do dia inteiro (Vittorio,
 * 09/08). O roteiro por extenso continua existindo — vai no `title`, via
 * `roteiroCompleto`, e nas telas que têm largura.
 */
function nomeViagem(vg: ViagemLite): string {
  return nomeCurtoDaViagem(vg.nome_curto, vg.destinos) || `Viagem #${vg.id}`;
}

/** O roteiro por extenso, para o `title` de quem mostra o nome curto. */
function roteiroDaViagem(vg: ViagemLite): string {
  return roteiroCompleto(vg.destinos) || `Viagem #${vg.id}`;
}

/**
 * Tipo de serviço → cor. É o sinal principal do cartão e do evento; o ícone é apoio.
 * A planilha dela não tem código de cor (conferido célula a célula), então somos livres.
 *
 * **Nenhuma cor é escrita aqui, de propósito** — só nomes de classe do AdminUX. Quem
 * define o tom é o tema, então mexer na aparência do app leva a agenda junto. Hex
 * cravado no componente (o que este arquivo fazia até 17/07) fura essa ligação: fica
 * bonito hoje e órfão na primeira troca de tema.
 *
 * - `tema`: classe `.theme-*` do AdminUX (`_variables.scss:342`). Ela redefine
 *   `--adminuiux-theme-1/2` no escopo do elemento, então o gradiente do cartão e a
 *   cor da faixa saem do tema sozinhos.
 * - `evento`: classe de fundo que a demo do tema (`theme/adminux-react/src/pages/Calendar.tsx`) usa nos eventos.
 *   O contraste do texto vem do `_calendar.scss:519` (`.fc-event-main`).
 */
const TIPO_INFO: Record<TipoOS, { icon: string; tema: string; evento: string }> = {
  instalacao: {
    icon: 'bi-hammer',
    tema: 'theme-blue',
    evento: 'bg-primary-subtle',
  },
  manutencao: {
    icon: 'bi-wrench',
    tema: 'theme-yellow',
    evento: 'bg-orange-subtle',
  },
  lavagem: {
    icon: 'bi-droplet',
    tema: 'theme-cyan',
    evento: 'bg-cyan-subtle',
  },
};

/**
 * Taxonomia do evento AVULSO (Fatia 2b). Ícone + cor de fundo por tipo — a mesma
 * regra do `TIPO_INFO`: **zero hex**, só classes `-subtle` do tema. Paleta escolhida
 * pra NÃO colidir com a das OS (azul/laranja/ciano): o avulso puxa amarelo/verde/
 * turquesa/índigo/cinza/roxo, e o ícone reforça a distinção quando a cor se aproxima.
 * `os` não entra aqui — visita de OS herda a cor do tipo da OS (`TIPO_INFO`).
 */
/** Quantos eventos cabem num dia antes de ele ser cortado (e ganhar o "+N ⌄"). */
const MAX_EVENTOS_DIA = 5;

const TIPO_EVENTO: Record<
  Exclude<TipoEvento, 'os'>,
  { icon: string; evento: string; label: string }
> = {
  entrega: { icon: 'bi-box-seam', evento: 'bg-green-subtle', label: 'Entrega' },
  retirada: {
    icon: 'bi-box-arrow-in-down',
    evento: 'bg-teal-subtle',
    label: 'Retirada',
  },
  servico: { icon: 'bi-tools', evento: 'bg-indigo-subtle', label: 'Serviço na loja' },
  ausencia: {
    icon: 'bi-person-dash',
    evento: 'bg-secondary-subtle',
    label: 'Ausência',
  },
  reuniao: { icon: 'bi-people', evento: 'bg-purple-subtle', label: 'Reunião' },
  // `medicao` entrou no vocabulário em 23/09, DEPOIS desta tela ser
  // congelada. A linha existe só para o TypeScript fechar o Record — a
  // tela congelada não se corrige, o valor dela é ser o que estava no ar.
  medicao: { icon: 'bi-rulers', evento: 'bg-pink-subtle', label: 'Medição' },
  /* "Dia sem OS": não é compromisso, é a afirmação de que o dia não tem
     instalação (Vittorio, 31/07). Por isso não ganha cor de categoria — o visual
     dele é a hachura (`evento-dia-sem-os`), que o tira da fila de tarefas. */
  dia_sem_os: {
    icon: 'bi-calendar-x',
    evento: 'bg-secondary-subtle',
    label: 'Dia sem OS',
  },
  // Ícone RETO (bi-sticky, um post-it) — o pin inclinado (bi-pin-angle) não fica
  // equilibrado no card (feedback do Vittorio, 22/07).
  lembrete: { icon: 'bi-sticky', evento: 'bg-yellow-subtle', label: 'Lembrete' },
};

/**
 * Cor do tipo em TOKEN do tema (Bootstrap `--bs-<cor>`), pra barra lateral do
 * estilo "Barra" — sem cravar hex. OS herda a cor do tipo da OS; avulso, do seu tipo.
 */
const COR_BS_OS: Record<TipoOS, string> = {
  instalacao: 'blue',
  manutencao: 'orange',
  lavagem: 'cyan',
};
const COR_BS_EVENTO: Record<Exclude<TipoEvento, 'os'>, string> = {
  entrega: 'green',
  retirada: 'teal',
  servico: 'indigo',
  ausencia: 'gray',
  reuniao: 'purple',
  medicao: 'pink',
  dia_sem_os: 'gray',
  lembrete: 'yellow',
};
function corBsDoEvento(v: VisitaAgenda): string {
  if (v.os_id) return v.os_tipo ? COR_BS_OS[v.os_tipo] : 'gray';
  const t = (v.tipo ?? 'lembrete') as Exclude<TipoEvento, 'os'>;
  return COR_BS_EVENTO[t] ?? 'yellow';
}

/** Ícone + rótulo do evento avulso pro cabeçalho do resumão ('os' nunca chega aqui). */
function infoEvento(tipo?: TipoEvento): { icon: string; label: string } {
  const t = tipo && tipo !== 'os' ? tipo : 'lembrete';
  return TIPO_EVENTO[t] ?? TIPO_EVENTO.lembrete;
}

/** O "editar tudo" mudou algo em relação ao que está na agenda? Compara só o que
 *  o modal edita — é o gatilho do aviso de "fechou sem salvar". */
function editSujo(ed: VisitaAgenda, orig: VisitaAgenda): boolean {
  const t = (s?: string | null) => (s ?? '').trim();
  const ids = (xs: Instalador[]) =>
    xs
      .map((x) => x.id)
      .sort((a, b) => a - b)
      .join(',');
  return (
    t(ed.titulo) !== t(orig.titulo) ||
    (ed.tipo ?? null) !== (orig.tipo ?? null) ||
    (ed.custo ?? 'a_definir') !== (orig.custo ?? 'a_definir') ||
    (ed.data || null) !== (orig.data || null) ||
    (ed.hora || null) !== (orig.hora || null) ||
    ed.status !== orig.status ||
    (ed.prioridade ?? 'normal') !== (orig.prioridade ?? 'normal') ||
    (ed.motivo ?? null) !== (orig.motivo ?? null) ||
    t(ed.pauta) !== t(orig.pauta) ||
    t(ed.desfecho) !== t(orig.desfecho) ||
    t(ed.endereco) !== t(orig.endereco) ||
    t(ed.numero) !== t(orig.numero) ||
    t(ed.complemento) !== t(orig.complemento) ||
    t(ed.bairro) !== t(orig.bairro) ||
    t(ed.cidade) !== t(orig.cidade) ||
    t(ed.uf) !== t(orig.uf) ||
    t(ed.cep) !== t(orig.cep) ||
    ids(ed.instaladores) !== ids(orig.instaladores)
  );
}

/** Corpo do "editar tudo": o evento AVULSO ganha abas (Evento | Endereço) —
 *  decisão do Vittorio (29/07), já que o endereço completo não cabe junto.
 *  Visita de OS segue sem abas: o endereço dela vem da OS.
 *
 *  Abas no estilo TRADICIONAL do tema (`nav-tabs adminuiux-tabs`), não pills.
 *  A altura é travada na da aba mais alta já vista: trocar de aba não pode
 *  redimensionar o modal (o conteúdo pular embaixo do cursor é desagradável).
 *  Só cresce — nunca encolhe enquanto o modal está aberto. */
function AbasEvento({
  avulso,
  endereco,
  children,
}: {
  avulso: boolean;
  endereco: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [altura, setAltura] = useState<number>();

  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight;
    if (h) setAltura((a) => (a && a >= h ? a : h));
  });

  if (!avulso) return <>{children}</>;
  return (
    <div ref={ref} style={altura ? { minHeight: altura } : undefined}>
      <Tabs className="adminuiux-tabs mb-3" defaultActiveKey="evento">
        <Tab eventKey="evento" title="Evento">
          {children}
        </Tab>
        <Tab eventKey="endereco" title="Endereço">
          {endereco}
        </Tab>
      </Tabs>
    </div>
  );
}

/** Sigla curta do tipo de OS pro card (INS/MAN/LAV) — legível sem depender do ícone. */
const ABREV_OS: Record<TipoOS, string> = {
  instalacao: 'INS',
  manutencao: 'MAN',
  lavagem: 'LAV',
};

/** Ordem de exibição dos tipos de evento no seletor (lembrete por último: é o "resto"). */
const TIPOS_EVENTO: Exclude<TipoEvento, 'os'>[] = [
  'entrega',
  'retirada',
  'servico',
  'ausencia',
  'reuniao',
  'dia_sem_os',
  'lembrete',
];

/** Marcador "gera custo?" — mesmo trio da cobrança da OS, mas é custo pra empresa. */
const CUSTO_OPCOES: { value: CustoEvento; label: string }[] = [
  { value: 'a_definir', label: 'A definir' },
  { value: 'sim', label: 'Gera custo' },
  { value: 'nao', label: 'Sem custo' },
];

/** Cliente pode vir vazio (ex.: BZ-2026-0064). Nunca renderizar linha em branco. */
const nomeCliente = (n?: string | null) => (n && n.trim() ? n : 'Sem cliente');

/**
 * Date (local) → "AAAA-MM-DD" **no fuso local**.
 * Não usar `toISOString().slice(0,10)`: aquilo converte pra UTC e devolve o dia
 * errado em fuso positivo. Aqui em Brasília (-3) dava certo por sorte, e uma agenda
 * que erra o dia por causa de fuso é um bug caro de achar depois.
 */
const paraIsoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

const LOJAS = [
  { codigo: '', nome: 'Todas' },
  { codigo: 'BZ', nome: 'Búzios' },
  { codigo: 'TR', nome: 'Trancoso' },
];

const VAZIO = {
  os_id: 0,
  data_inicio: '',
  data_fim: '',
  hora: '',
  instaladores: [] as number[],
  status: 'agendada' as OSVisita['status'],
  pauta: '',
};

/**
 * Realce das datas da OS em foco + destaques da visão "5 semanas". Fica aqui (e não
 * no SCSS do tema) porque é exclusivo desta tela de teste — se a tela morrer, o CSS
 * morre junto.
 *
 * Tudo aninhado sob `.fc`: o tema estiliza as células com seletores fundos
 * (`.fc ... td.fc-day`), e uma classe solta minha perdia em especificidade e não
 * pintava nada — o fundo saía transparente apesar da classe estar aplicada. Prefixar
 * `.fc` empata a especificidade sem recorrer a `!important` em toda regra.
 */
const CSS_LOCAL = `
/* Speed dial da pílula de viagem: leque de ações que sai do ponto do clique. */
.sd-backdrop { position: fixed; inset: 0; z-index: 1080; background: transparent; }
.sd-dial {
  position: fixed; z-index: 1081; display: flex; flex-direction: column;
  align-items: flex-start; gap: 8px;
}
.sd-item {
  display: flex; align-items: center; gap: 8px; border: 0; background: transparent;
  padding: 0; cursor: pointer; transform-origin: left center;
  opacity: 0; transform: translateY(-8px) scale(.85);
  transition: opacity .16s ease, transform .16s ease;
}
.sd-item.sd-on { opacity: 1; transform: translateY(0) scale(1); }
/* Flip pra cima quando o botão está perto da base da tela. */
.sd-dial.sd-up { flex-direction: column-reverse; }
.sd-dial.sd-up .sd-item { transform: translateY(8px) scale(.85); }
.sd-dial.sd-up .sd-item.sd-on { transform: translateY(0) scale(1); }
.sd-item .sd-ic {
  flex: 0 0 auto; width: 38px; height: 38px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 10px rgba(0,0,0,.22); font-size: 1rem;
}
.sd-item .sd-lb {
  font-size: 13px; font-weight: 500; white-space: nowrap;
  padding: 5px 11px; border-radius: 9px; box-shadow: 0 2px 10px rgba(0,0,0,.16);
}
.sd-item:hover .sd-ic { filter: brightness(1.08); }
@keyframes piscaDiaOS {
  0%, 100% { box-shadow: inset 0 0 0 2px var(--bs-primary); }
  50%      { box-shadow: inset 0 0 0 2px transparent; }
}
.fc .dia-da-os { animation: piscaDiaOS 1.2s ease-in-out infinite; }
/* O evento da OS em foco já troca de cor (bg-theme-1-space); o pulso do contorno
   é o que faz o olho achar ele no meio dos outros. */
@keyframes pulsaEventoOS {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.04); }
}
.fc .evento-da-os {
  outline: 2px solid var(--bs-primary);
  outline-offset: 1px;
  animation: pulsaEventoOS 1.2s ease-in-out infinite;
  z-index: 5;
}

/* Grade do calendário um tom mais escura — o --bs-border-color do tema é bem claro
   e a grade quase somia. Especificidade casada com a do tema (.fc.fc-theme-standard
   table td) + 1 pra vencer. */
.fc.fc-theme-standard table td,
.fc.fc-theme-standard table th { border-color: rgba(var(--bs-secondary-rgb), .32); }

/* Semana corrente (1ª linha da "5 semanas"): destaque só pela BORDA (topo+base da
   faixa), sem fundo — o fundo fica reservado à diferença de mês (fc-day-other).
   box-shadow inset em vez de border: a borda de célula COLAPSA com a da vizinha
   (a de baixo sumia no 5 semanas); o inset desenha dentro da célula e sempre aparece,
   nas duas visões. Sem laterais, de propósito. */
.fc td.fc-day.semana-atual {
  box-shadow: inset 0 2px 0 0 var(--bs-primary), inset 0 -2px 0 0 var(--bs-primary);
}
/* Virada de mês: barra forte onde um mês acaba e outro começa. */
.fc td.fc-day.primeiro-do-mes { border-left: 2px solid var(--bs-primary); }

/* Número do dia 15% maior (14px do tema -> 16px) — pedido do Vittorio, ajuda a
   bater o olho na data. Seletor casado com o do tema
   (.fc.fc-theme-standard table td .fc-daygrid-day-number, _calendar.scss) + a classe
   .fc-day pra vencer a especificidade; senão o 14px do tema prevalece. */
.fc.fc-theme-standard table td.fc-day .fc-daygrid-day-number { font-size: 16px; }
/* Ícones/rótulos de ação do evento (relógio, hora, expandir, duplicar, excluir):
   força tudo ao MESMO tamanho. Sem isso o ícone bi sai a 16px e a hora a 12px —
   desalinham e "não ficam no mesmo tamanho". O .fc vence a especificidade do tema
   que põe o ícone em 16px. */
.fc .evento-acao,
.fc .evento-acao i,
.fc .evento-acao span { font-size: 12px; line-height: 1; }
/* Cartão da faixa indo/voltando ao trancar (o olho não pega a reordenação seca).
   "saindo": esmaece e desliza pra fora. "chegando": pulsa um anel no novo lugar.
   A opacidade animada vence a opacity inline do cartão trancado. */
@keyframes cardSaindo {
  to { opacity: .3; transform: translateX(16px) scale(.95); }
}
@keyframes cardChegando {
  0%   { box-shadow: 0 0 0 3px var(--bs-primary); }
  100% { box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0); }
}
.card-saindo { animation: cardSaindo .42s ease forwards; }
.card-chegando { animation: cardChegando .7s ease; }

/* Botão de ação do evento: reset sem o azul do btn-link. Herda a cor do texto do
   evento (preta no normal, branca no evento em foco) — a "cor do sistema" que o
   Vittorio pediu, sem cor cravada. */
.fc .evento-botao {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.fc .evento-botao:hover { opacity: .7; }

/* Evento avulso (sem OS): fundo amarelo do tema (classe bg-yellow-subtle) + uma
   moldura âmbar sutil pra ler como objeto distinto do card de OS, com bom contraste.
   O .fc-event no seletor vence a borda transparente que o tema põe no evento. */
.fc .fc-event.evento-avulso,
.fc .evento-avulso {
  border: var(--bs-border-width) solid rgba(var(--bs-warning-rgb), .5);
  color: var(--adminuiux-text);
}

/* ── Estilo "Barra" (experimento 22/07, atrás do toggle "Aparência") ──────────
   Reusa o que o TEMA já dá no evento (_calendar.scss:471 — border + padding +
   radius). Fundo cinza DISCRETO (igual pra todos, nunca branco) + a barra é a
   BORDA ESQUERDA de verdade do card (colada na borda, não flutuando). A cor sai
   por CLASSE (evento-barra-<cor>) pra ser reativa ao toggle. Token do tema, sem hex. */
.fc .fc-event.evento-barra {
  background-color: var(--bs-secondary-bg);
  border-color: var(--bs-border-color);
  border-left-width: 4px;
  color: var(--adminuiux-text);
}
.fc .fc-event.evento-barra-blue   { border-left-color: var(--bs-blue); }
.fc .fc-event.evento-barra-orange { border-left-color: var(--bs-orange); }
.fc .fc-event.evento-barra-cyan   { border-left-color: var(--bs-cyan); }
.fc .fc-event.evento-barra-green  { border-left-color: var(--bs-green); }
.fc .fc-event.evento-barra-teal   { border-left-color: var(--bs-teal); }
.fc .fc-event.evento-barra-indigo { border-left-color: var(--bs-indigo); }
.fc .fc-event.evento-barra-purple { border-left-color: var(--bs-purple); }
.fc .fc-event.evento-barra-yellow { border-left-color: var(--bs-yellow); }
.fc .fc-event.evento-barra-gray   { border-left-color: var(--bs-gray); }
/* Categoria: OS usa o cinza clarinho (acima); o EVENTO avulso usa um creme (amarelo
   clarinho) pra bater o olho e saber que NÃO é uma OS. Depois das cores, pra vencer.
   NOTA (22/07): estes dois são tokens do Bootstrap que NÃO flipam no dark do AdminUX
   (o dark só redefine --adminuiux-*). Ficou ótimo no CLARO; o dark fica pra próxima
   conversa (ver memória agenda-barra-cores-dark). Reverter aqui foi decisão do Vittorio. */
.fc .fc-event.evento-barra-avulso { background-color: var(--bs-warning-bg-subtle); }

/* "+" no topo de cada dia pra criar um evento avulso (no lugar de clicar no espaço
   em branco, que era ruim de acertar). Vive dentro do número do dia (dayCellContent);
   pra ele ir à ESQUERDA e o número à direita, o link do número ocupa a largura toda.
   Discreto por padrão; ganha cor ao passar o mouse no dia. */
/* O padding-bottom equaliza a respiracao do numero do dia: medido no DOM em
   09/08 havia 10px acima dele e 5px abaixo (os 5 de baixo vem do proprio
   FullCalendar, no harness do evento). Somando 5 aqui, os dois lados ficam em
   10px e o numero para de parecer colado no primeiro card. */
.fc .fc-daygrid-day-top { width: 100%; padding-top: 6px; padding-bottom: 5px; padding-inline: 4px; }
.fc a.fc-daygrid-day-number { width: 100%; }
/* Todos os números do dia na MESMA linha horizontal. O avatar do hoje tem 30px de
   altura; os outros números são só texto (~19px). Como o FullCalendar alinha pelo
   topo, o círculo do hoje empurrava o próprio número (e os eventos daquele dia) uns
   px pra baixo — o "21 parece abaixo do 20/22". Fixamos a altura do topo do dia =
   altura do avatar em TODO dia; o .dia-topo já é align-items-center, então texto e
   avatar centralizam na mesma horizontal e os eventos começam todos na mesma altura. */
/* TRES COLUNAS, e nao flex com space-between (Vittorio, 09/08). Com flex, a
   pilula do meio ficava centrada ENTRE os vizinhos: quando os botoes de marcar
   entraram a esquerda, ela escorregou pra direita. Com 1fr auto 1fr a coluna do
   meio cai no centro do CARD, nao importa o tamanho das laterais.
   ATENCAO: nada de crase neste bloco - ele mora dentro de template string. */
.fc .dia-topo {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  min-height: 30px;
}
.fc .dia-topo-esq { justify-self: start; }
.fc .dia-topo-dir { justify-self: end; }
/* min-width:0 deixa a pilula encolher (o text-truncate dela so funciona se o
   item do grid puder ficar menor que o conteudo). */
.fc .dia-topo-meio {
  justify-self: center;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
}
.fc .btn-novo-evento {
  color: var(--bs-secondary);
  opacity: .55;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: opacity .15s ease, color .15s ease;
}
.fc .fc-daygrid-day:hover .btn-novo-evento { opacity: 1; color: var(--bs-primary); }
.fc .btn-novo-evento:hover { opacity: 1; color: var(--bs-primary); }

/* MARCADO para o instalador. As ações do dia vivem em 55% de opacidade, e é o
   certo para "+" e impressora: são convites, aparecem no hover. Marcação NÃO é
   convite, é ESTADO — precisa se ler de longe, varrendo o mês, sem passar o
   mouse (o Vittorio viu a 1a versão e disse: "está muito clarinho"). Cheio =
   cor do tema com fundo; parcial = mesma cor, sem fundo, para não se confundir
   com o dia inteiro marcado. */
.fc .btn-novo-evento.liberado-cheio,
.fc .fc-daygrid-day:hover .btn-novo-evento.liberado-cheio {
  opacity: 1;
  color: var(--adminuiux-theme-1);
  background-color: rgba(var(--adminuiux-theme-1-rgb), .12);
  border-radius: var(--bs-border-radius-sm);
  padding: 1px 3px;
}
.fc .btn-novo-evento.liberado-parcial,
.fc .fc-daygrid-day:hover .btn-novo-evento.liberado-parcial {
  opacity: .85;
  color: var(--adminuiux-theme-1);
}

/* Pílula "Livre" / "Livre de instalações" no topo do dia. Deliberadamente FRACA:
   fundo nenhum, contorno fino, letra secundária — o oposto da pílula da viagem,
   que é sólida. Numa grade de 5 semanas a maioria dos dias está livre; se isso
   fosse pintado forte, o destaque morreria justamente onde ele conta (a viagem, o
   dia cheio). Cor vem de token, então acompanha claro/escuro sozinha. */
.fc .pilula-livre {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--bs-border-color);
  color: var(--bs-secondary);
  font-size: 11px;
  padding: 0 .5rem;
  opacity: .85;
}
/* "Livre de instalações" pesa mais que "Livre": o dia vazio é o estado normal e
   não precisa ser lido; já um dia que TEM compromisso e mesmo assim não tem
   instalação é uma informação — é onde cabe encaixar trabalho. */
.fc .pilula-livre.pilula-livre-parcial {
  color: var(--adminuiux-text);
  border-color: var(--bs-secondary);
  opacity: 1;
}

/* "Dia sem OS": o card tem de PARAR de parecer tarefa. Hachura diagonal leve em
   cima do fundo do card — o padrão universal de "área marcada, nada a fazer aqui".
   A cor sai do token secundario via -rgb (é assim que o tema monta todos os fundos
   suaves), então acompanha claro/escuro sem cor cravada. */
.fc .fc-event.evento-dia-sem-os {
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(var(--bs-secondary-rgb), .10) 0,
    rgba(var(--bs-secondary-rgb), .10) 6px,
    transparent 6px,
    transparent 12px
  );
  color: var(--bs-secondary);
}

/* Um respiro entre os cards de compromisso (o tema deixa 1px, ficam colados). */
.fc .fc-daygrid-event-harness { margin-bottom: 4px; }

/* "recolher": é um evento do calendário só pra cair no fim do dia, no lugar
   exato onde estava o "+N ⌄". Então tem de PARAR de parecer um evento — sem
   fundo, sem borda, sem sombra — e virar um link discreto, com o mesmo peso
   visual do "+N" que o FullCalendar desenha (.fc-daygrid-more-link). */
.fc .fc-event.evento-recolher {
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 0;
}
.fc .link-recolher {
  color: var(--bs-secondary);
  font-size: 12px;
  line-height: 1.6;
  cursor: pointer;
  padding: 0 2px;
}
.fc .link-recolher:hover { color: var(--bs-primary); text-decoration: underline; }

/* Linha de metadado do modal do evento (endereço, status, quem vai): coluna de
   ícone com largura FIXA, pra texto alinhar com texto. Era o desalinho visível
   quando o rótulo tinha ícone e o texto de baixo não — o "Nada marcado…"
   encostado na borda esquerda. */
.linha-meta { display: flex; gap: .5rem; align-items: flex-start; }
.linha-meta > i:first-child { flex: 0 0 1rem; text-align: center; line-height: 1.5; }

/* Os dois modais de evento (ver e editar) têm a MESMA largura — são a mesma
   conversa em dois momentos. Um pouco menores que o modal-lg (800px): com
   700px o texto não corre a tela toda e o modal ainda cabe folgado no conteúdo.
   Só a partir de lg: abaixo disso o Bootstrap já manda no tamanho.
   ATENÇÃO: nada de crase neste bloco — ele mora dentro de um template string. */
@media (min-width: 992px) {
  .modal-evento { max-width: 700px; }
}

/* Aviso de "isto sai no papel": ganha a cor do tema, não mais um terceiro
   cinza. Cinza já é placeholder e legenda — três cinzas empilhados não deixam
   ver o que é campo, o que é dica e a qual campo a dica pertence (Vittorio,
   31/07). Cor + ícone resolvem sem acrescentar mais texto. */
.aviso-roteiro {
  color: var(--adminuiux-theme-1);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  /* Alinha com o texto de DENTRO do campo (o form-control tem .75rem de padding
     horizontal) — senão o aviso fica um dedo à esquerda da caixa. */
  padding-left: .75rem;
}
/* Duas classes de propósito: o text-secondary sozinho perderia pra regra
   acima dependendo da ordem em que o CSS entra. */
.aviso-roteiro.aviso-mudo { color: var(--bs-secondary); }

/* Dia de outro mês: o número somia. A CAUSA REAL é o FullCalendar pôr
   opacity:0.3 no .fc-daygrid-day-top (o container do número) — e opacidade
   multiplica, então mexer na cor/opacidade do número não adiantava, o pai a 30%
   derrubava tudo. Devolvemos o top a opacity:1 e damos ao número um tom próprio,
   pra ficar claramente do "mês de fora" sem ficar invisível. O fundo cinza segue. */
.fc td.fc-day.fc-day-other .fc-daygrid-day-top { opacity: 1; }
.fc td.fc-day.fc-day-other .fc-daygrid-day-number { color: var(--adminuiux-text); opacity: .7; }

/* Dia de HOJE: o tema só marca o hoje com um fundo levíssimo (--fc-today-bg-color) e,
   na "5 semanas", a semana inteira já tem destaque (semana-atual) — o dia se perdia lá
   dentro. O círculo no número é o ponto focal. NÃO é desenho próprio: é o componente
   .avatar do tema (o mesmo que o dateselect do _calendar.scss usa), aplicado no JSX
   como "avatar avatar-30 rounded-circle bg-theme-1" (fundo do tema + texto claro, que a
   própria classe .bg-theme-1 já define). Aqui só damos o peso da fonte;
   tamanho/forma/cor vêm do tema. */
.fc .fc-daygrid-day-top .avatar { font-size: 14px; font-weight: 600; }
/* Piscada temporária ao clicar "Hoje": 3 ciclos (~1,5s) e para. O container do
   calendário ganha .pisca-hoje por 1,6s (controle no JS); iteration-count fixo (3)
   garante que a animação pare mesmo se o timer falhar. Isto é comportamento (foco
   momentâneo), não um componente do tema — por isso é CSS local. */
@keyframes piscaHojeAnim {
  0%, 100% { box-shadow: inset 0 0 0 3px var(--bs-primary); }
  50%      { box-shadow: inset 0 0 0 3px transparent; }
}
.pisca-hoje .fc td.fc-day-today { animation: piscaHojeAnim .5s ease-in-out 3; }

/* Botão "Desarquivar" do card travado (OS arquivada). Antes era w-100: encostava nas
   laterais e as bordas eram comidas pelo overflow:hidden do .fc-event — parecia um
   traço solto. Agora é uma pílula compacta e centralizada (wrapper com mt-2/mb-1 dá o
   respiro do rodapé). Tamanho casado com o das demais ações do card (12px). */
.fc .btn-desarquivar {
  font-size: 12px;
  line-height: 1.7;
}
.fc .btn-desarquivar i { font-size: 12px; }
`;

export function AgendaAgendamentosAntigaPage() {
  const navigate = useNavigate();
  const [visitas, setVisitas] = useState<VisitaAgenda[]>([]);
  const [osAgendar, setOsAgendar] = useState<OSAgendar[]>([]);
  const [instaladores, setInstaladores] = useState<Instalador[]>([]);
  const [viagens, setViagens] = useState<ViagemLite[]>([]);
  // Evento à espera de escolher a viagem, quando 2+ cobrem o mesmo dia (BZ e TR).
  const [escolhendoViagem, setEscolhendoViagem] = useState<{
    evento: VisitaAgenda;
    left: number;
    top: number;
    bottom: number;
  } | null>(null);
  // Speed dial de impressão de roteiro aberto a partir da pílula de viagem do dia.
  const [speed, setSpeed] = useState<{
    left: number;
    top: number;
    bottom: number;
    dia: string;
  } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [fimDeSemana, setFimDeSemana] = useState(false);
  // Dia cheio: em vez do popover do FullCalendar (que transbordava a tela e
  // escondia a lixeira do último evento — Suany, 30/07), a grade CRESCE. Só a
  // linha que tem evento demais estica; o resto fica como está. `dayMaxEvents`
  // é opção global do FullCalendar, então isto vale para a grade inteira.
  const [expandirDias, setExpandirDias] = useState(false);
  const [loja, setLoja] = useState('');
  const [busca, setBusca] = useState('');
  // Filtro do calendário por status do evento + urgência (Fatia 5a). Default: tudo
  // visível (a Suany esconde o que não quer ver — ex.: realizadas/canceladas).
  const [statusVisiveis, setStatusVisiveis] = useState<Set<OSVisita['status']>>(
    () => new Set(['agendada', 'realizada', 'nao_realizada', 'cancelada']),
  );
  const [soUrgentes, setSoUrgentes] = useState(false);
  // Filtros do painel "OS a agendar" (Fatia 5b): reexibem concluídas / arquivadas
  // (default só mostra o que falta agendar).
  const [incluirConcluidas, setIncluirConcluidas] = useState(false);
  const [incluirArquivadas, setIncluirArquivadas] = useState(false);
  function toggleStatusVisivel(s: OSVisita['status']) {
    setStatusVisiveis((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }
  const [estiloCard, setEstiloCard] = useState<'cartao' | 'detalhado'>('cartao');
  // Experimento (22/07): estilo do evento no calendário e o que é o "herói" do card.
  // Default = como está hoje (clássico + código). Reversível por clique. Ver o toggle
  // "Aparência" na barra do calendário.
  const [estiloEvento, setEstiloEvento] = useState<'classico' | 'barra'>('barra');
  const [tituloCard, setTituloCard] = useState<'codigo' | 'cliente'>('codigo');

  /** Resumo da OS: clicar no corpo de um evento abre um "resumão" (OSDetalhe). */
  const [resumoOs, setResumoOs] = useState<OSDetalhe | null>(null);
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  /**
   * A visita que originou o resumão. O resumo é da OS, mas quem foi clicado é uma
   * IDA específica — é a pauta dela que se edita ali dentro. Sem isso o modal não
   * saberia qual das visitas da OS o texto pertence.
   */
  const [resumoVisita, setResumoVisita] = useState<VisitaAgenda | null>(null);
  /** Editor da pauta dentro do resumão: null = só leitura; string = editando. */
  const [pautaEdit, setPautaEdit] = useState<string | null>(null);
  const [salvandoPauta, setSalvandoPauta] = useState(false);
  /** Eventos com a linha de ações expandida (por id da visita). */
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  /** Animação do cartão ao trancar/destrancar: 'saindo' (desliza pra fora) e depois
   *  'chegando' (pulsa no novo lugar). Sem isso a reordenação é instantânea demais
   *  pra o olho pegar. */
  const [movimento, setMovimento] = useState<{
    id: number;
    fase: 'saindo' | 'chegando';
  } | null>(null);

  /** OS "focada": clicar no cartão acende as datas dela no calendário. */
  const [osFoco, setOsFoco] = useState<number | null>(null);
  const calendarioRef = useRef<FullCalendar | null>(null);

  /**
   * Tela cheia (Fullscreen API do navegador): faixa + calendário ocupam o monitor
   * inteiro pra montar a agenda sem distração. O ESC sai sozinho — não programamos
   * isso; é o navegador. `telaCheia` é só o reflexo do estado real, mantido em sincs
   * pelo evento `fullscreenchange`, pra que sair pelo ESC também atualize o botão.
   */
  const areaRef = useRef<HTMLDivElement | null>(null);
  const [telaCheia, setTelaCheia] = useState(false);

  /** Piscada temporária no dia de hoje ao clicar "Hoje" (some após ~1,6s). */
  const [piscaHoje, setPiscaHoje] = useState(false);
  const piscaTimer = useRef<number | null>(null);

  /**
   * Controle do foco na TROCA de visão. O FullCalendar guarda uma só "data de
   * referência" e cada visão a interpreta com granularidade diferente (começo da
   * semana / dia 1º do mês / 1ª linha da 5 semanas) — por isso trocar de visão às
   * vezes pulava de mês "sem razão". Aqui a gente assume o controle: guarda o dia que
   * se está olhando (`focoRef`) e, ao detectar troca de visão (`tipoViewRef` mudou),
   * reancora a nova visão nesse dia — ela sempre mostra o período que contém o que
   * você via (e hoje, se hoje estava na tela).
   */
  const tipoViewRef = useRef<string | null>(null);
  const focoRef = useRef<string>(hojeISO());

  useEffect(() => {
    const onChange = () => setTelaCheia(document.fullscreenElement === areaRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  function alternarTelaCheia() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      areaRef.current?.requestFullscreen?.().catch(() => {
        toast.error('Seu navegador não permitiu a tela cheia.');
      });
    }
  }

  /**
   * "Hoje": volta ao ponto de partida — visão 5 semanas começando na semana atual —
   * e pisca o dia de hoje por ~1,6s pra o olho achar onde ele está. É botão próprio
   * (customButton) e NÃO o `today` nativo, que o FullCalendar DESABILITA quando o hoje
   * já está à vista — o que dava a impressão de "botão que não funciona".
   */
  function irParaHoje() {
    const cal = calendarioRef.current?.getApi();
    if (!cal) return;
    focoRef.current = hojeISO(); // pra a troca de visão reancorar em hoje, não no foco antigo
    cal.changeView('cincoSemanas');
    cal.today();
    if (piscaTimer.current) window.clearTimeout(piscaTimer.current);
    setPiscaHoje(true);
    piscaTimer.current = window.setTimeout(() => setPiscaHoje(false), 1600);
  }
  useEffect(
    () => () => {
      if (piscaTimer.current) window.clearTimeout(piscaTimer.current);
    },
    [],
  );
  /** Intervalo e visão que o calendário está exibindo — pra saber o que ficou fora
   *  da vista e quais realces valem em cada uma. */
  const [vista, setVista] = useState<{
    de: string;
    ate: string;
    tipo: string;
  } | null>(null);

  const [formNova, setFormNova] = useState(VAZIO);
  const [agendando, setAgendando] = useState(false);
  /** Form do evento avulso (sem OS): nasce pelo "+" no topo de um dia. */
  const [formEvento, setFormEvento] = useState<{
    data_inicio: string;
    hora: string;
    tipo: Exclude<TipoEvento, 'os'>;
    titulo: string;
    custo: CustoEvento;
    pauta: string;
    instaladores: number[];
    status: OSVisita['status'];
  } | null>(null);
  const [criandoEvento, setCriandoEvento] = useState(false);
  const [editando, setEditando] = useState<VisitaAgenda | null>(null);
  const [salvandoEdit, setSalvandoEdit] = useState(false);
  /** Confirmação de exclusão (modal do tema, no lugar do window.confirm nativo). */
  const [visitaExcluir, setVisitaExcluir] = useState<VisitaAgenda | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [arrastando, setArrastando] = useState<OSAgendar | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  /** Semana corrente (segunda a domingo) — a 1ª linha da visão "5 semanas". */
  const semanaAtual = useMemo(() => {
    const de = inicioSemana(hojeISO());
    return { de, ate: addDias(de, 6) };
  }, []);

  /**
   * Mês "principal" da 5 semanas = o **mês de hoje**, o mês em que se está montando
   * agenda. Os outros meses ficam acinzentados (só "diferença de mês", como o Vittorio
   * pediu). Antes usávamos o mês MAJORITÁRIO, mas a 5 semanas começa na semana atual e
   * se estende pra frente — então o mês seguinte virava maioria e o mês atual (o perto,
   * o que importa) ficava todo cinza. Ancorar em hoje conserta: mês atual limpo, o
   * seguinte cinza.
   */
  const mesPrincipal = useMemo(() => hojeISO().slice(0, 7), []);

  /**
   * A célula abre um mês novo **na tela**? Não basta perguntar "é dia 1?": com a
   * grade em Seg–Sex, todo dia 1 que cai em fim de semana some, e a virada passaria
   * batido (1º/08/2026 é sábado — foi assim que este caso apareceu). Então comparamos
   * com o dia útil anterior exibido: se mudou de mês, esta célula abre o mês.
   */
  const abreMesNaTela = useCallback(
    (d: Date) => {
      const iso = paraIsoLocal(d);
      const anterior =
        !fimDeSemana && d.getDay() === 1 ? addDias(iso, -3) : addDias(iso, -1);
      return iso.slice(0, 7) !== anterior.slice(0, 7);
    },
    [fimDeSemana],
  );

  /**
   * `comSpinner` só na primeira carga. Recarregar com spinner troca o calendário
   * por um "Carregando…", o que **desmonta e remonta o FullCalendar** — é isso que
   * fazia a tela piscar a cada visita movida. Depois da 1ª vez, recarrega calado.
   */
  const carregarVisitas = useCallback(async (comSpinner = false) => {
    if (comSpinner) setCarregando(true);
    try {
      const hoje = new Date();
      const de = new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1);
      const ate = new Date(hoje.getFullYear(), hoje.getMonth() + 4, 0);
      const iso = (d: Date) => d.toISOString().slice(0, 10);
      setVisitas(
        await api.get<VisitaAgenda[]>(`/agenda/visitas?de=${iso(de)}&ate=${iso(ate)}`),
      );
    } catch {
      /* silencioso */
    } finally {
      if (comSpinner) setCarregando(false);
    }
  }, []);

  /* ── Marcar o que o instalador vê (migration 047) ───────────────────────────
     Só existe estado no CARD. Dia e semana são ATALHOS: marcam ou desmarcam em
     lote os cards do intervalo, e não guardam nada sobre o período. É o que
     permite marcar o dia e depois tirar um compromisso dele — a regra dela.

     Por isso o olho do cabeçalho é DERIVADO dos cards daquele dia: apagado
     (nenhum), parcial (alguns) ou aceso (todos). E é isso que também resolve o
     redesenho: mexer na marcação muda os EVENTOS, que é a única coisa que faz
     o FullCalendar repintar o dia. */
  const contarMarcados = useCallback(
    (deIso: string, ateIso: string) => {
      const nos = visitas.filter((v) => {
        const d = String(v.data ?? '').slice(0, 10);
        return d >= deIso && d <= ateIso;
      });
      return { total: nos.length, marcados: nos.filter((v) => v.liberado).length };
    },
    [visitas],
  );

  /** 'vazio' | 'parcial' | 'cheio' — as três aparências do olho. */
  const estadoLiberacao = useCallback(
    (deIso: string, ateIso: string): 'vazio' | 'parcial' | 'cheio' => {
      const { total, marcados } = contarMarcados(deIso, ateIso);
      if (!total || !marcados) return 'vazio';
      return marcados === total ? 'cheio' : 'parcial';
    },
    [contarMarcados],
  );

  /** Marca todos; se já estão todos marcados, desmarca todos. */
  const alternarEmLote = useCallback(
    async (escopo: 'dia' | 'semana', iso: string) => {
      const deIso = escopo === 'semana' ? inicioSemana(iso) : iso;
      const ateIso = escopo === 'semana' ? addDias(deIso, 6) : iso;
      const { total } = contarMarcados(deIso, ateIso);
      if (!total) return; // nada para marcar — o botão nem acende
      const alvo = estadoLiberacao(deIso, ateIso) !== 'cheio';
      const antes = visitas;
      setVisitas((as) =>
        as.map((v) => {
          const d = String(v.data ?? '').slice(0, 10);
          return d >= deIso && d <= ateIso ? { ...v, liberado: alvo } : v;
        }),
      );
      try {
        await api.put('/agenda/visitas/liberacoes', { escopo, data: deIso, liberado: alvo });
      } catch {
        setVisitas(antes);
        toast.error('Não foi possível mudar a liberação.');
      }
    },
    [visitas, contarMarcados, estadoLiberacao],
  );

  /** Um card só — inclusive dentro de um dia já marcado (é a exceção dela). */
  const alternarLiberacaoCard = useCallback(async (v: VisitaAgenda) => {
    const alvo = !v.liberado;
    setVisitas((as) =>
      as.map((x) => (x.id === v.id ? { ...x, liberado: alvo } : x)),
    );
    try {
      await api.put(`/agenda/visitas/${v.id}/liberacao`, { liberado: alvo });
    } catch {
      setVisitas((as) => as.map((x) => (x.id === v.id ? v : x)));
      toast.error('Não foi possível mudar a liberação.');
    }
  }, []);

  const carregarOsAgendar = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (incluirConcluidas) q.set('incluir_concluidas', 'true');
      if (incluirArquivadas) q.set('incluir_arquivadas', 'true');
      const qs = q.toString();
      setOsAgendar(
        await api.get<OSAgendar[]>(
          `/agenda/ordens-servico${qs ? `?${qs}` : ''}`,
        ),
      );
    } catch {
      /* silencioso */
    }
  }, [incluirConcluidas, incluirArquivadas]);

  // Só viagem operacional entra na agenda dos instaladores (sócias/administrativa
  // não aparecem — decisão 22/07).
  const carregarViagens = useCallback(async () => {
    try {
      setViagens(await api.get<ViagemLite[]>('/viagens?tipo=operacional'));
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    carregarVisitas(true);
    carregarOsAgendar();
    carregarViagens();
    api
      .get<Instalador[]>('/agenda/visitas/instaladores')
      .then(setInstaladores)
      .catch(() => undefined);
  }, [carregarVisitas, carregarOsAgendar, carregarViagens]);

  /**
   * Filtro da faixa: loja + busca por texto. A busca filtra **enquanto digita**
   * (sem Enter, sem botão) e olha código da OS, cliente e pedido — os três jeitos
   * de procurar uma OS na planilha dela. Sem debounce de propósito: a lista já está
   * na memória, filtrar é instantâneo e adiar só atrasaria o retorno visual.
   */
  const osFiltradas = useMemo(() => {
    const daLoja = loja
      ? osAgendar.filter((o) => o.loja_codigo === loja)
      : osAgendar;
    const q = busca.trim().toLowerCase();
    if (!q) return daLoja;
    return daLoja.filter((o) =>
      [o.codigo, o.cliente_nome, o.pedido]
        .filter(Boolean)
        .some((campo) => campo!.toLowerCase().includes(q)),
    );
  }, [osAgendar, loja, busca]);
  const visitasFiltradas = useMemo(
    () =>
      visitas.filter(
        (v) =>
          (!loja || v.loja_codigo === loja) &&
          statusVisiveis.has(v.status) &&
          (!soUrgentes || v.prioridade === 'alta'),
      ),
    [visitas, loja, statusVisiveis, soUrgentes],
  );

  /** Datas com visita da OS em foco — e as que caíram fora do mês à vista. */
  const datasDaOsFoco = useMemo(
    () =>
      osFoco
        ? visitas.filter((v) => v.os_id === osFoco && v.data).map((v) => v.data!)
        : [],
    [visitas, osFoco],
  );
  const datasForaDaVista = useMemo(
    () =>
      vista ? datasDaOsFoco.filter((d) => d < vista.de || d > vista.ate) : [],
    [datasDaOsFoco, vista],
  );

  function abrirAgendar(os: OSAgendar, data: string) {
    // Pré-marca todos os instaladores ativos: o normal é a equipe inteira ir
    // junto; a pessoa só desmarca a exceção (foi um só, ou entrou um ajudante).
    setFormNova({
      ...VAZIO,
      os_id: os.id,
      data_inicio: data,
      instaladores: instaladores.map((i) => i.id),
    });
  }

  /**
   * Foca (ou desfoca) uma OS pelo id e **leva o calendário até a 1ª visita dela**.
   * Focar acende TODAS as visitas da OS (pisca) e destaca o cartão dela na faixa —
   * por isso o pin no rodapé do evento chama isto: um jeito de, a partir de uma
   * visita, ver onde estão as outras da mesma OS.
   *
   * Sem o `gotoDate`, o destaque só "funcionava" no mês: a semana e a lista mostram
   * a semana de hoje, e a visita da OS quase nunca cai nela — daí a impressão de que
   * o destaque some ao trocar de visão. Não era o realce falhando; não havia o que
   * realçar na tela.
   */
  function focarOs(osId: number) {
    if (osFoco === osId) {
      setOsFoco(null);
      return;
    }
    setOsFoco(osId);
    const datas = visitas
      .filter((v) => v.os_id === osId && v.data)
      .map((v) => v.data!)
      .sort();
    if (datas.length) calendarioRef.current?.getApi().gotoDate(datas[0]);
  }

  /** Abre a OS avisando de onde veio, pra que o "Voltar" de lá devolva pra cá.
   *  O form da OS lê `state.voltarPara` e cai na lista quando ninguém diz nada. */
  function abrirOS(osId: number) {
    navigate(`/ordens-servico/${osId}`, {
      state: { voltarPara: '/agenda/teste-1' },
    });
  }

  function celulaEm(x: number, y: number): HTMLElement | null {
    for (const el of document.elementsFromPoint(x, y)) {
      const cel = el.closest('[data-date]');
      if (cel) return cel as HTMLElement;
    }
    return null;
  }

  /**
   * Realce do dia sob o cursor. Mexemos no DOM direto (e não por estado) de
   * propósito: isto roda a cada pixel do arrasto, e re-renderizar o FullCalendar
   * inteiro nesse ritmo engasga a tela.
   */
  const diaRealcado = useRef<HTMLElement | null>(null);
  function realcar(el: HTMLElement | null) {
    if (diaRealcado.current === el) return;
    if (diaRealcado.current) {
      diaRealcado.current.style.outline = '';
      diaRealcado.current.style.outlineOffset = '';
    }
    if (el) {
      el.style.outline = '2px solid var(--bs-primary)';
      el.style.outlineOffset = '-2px';
    }
    diaRealcado.current = el;
  }

  function onDragStart(e: DragStartEvent) {
    setArrastando((e.active.data.current?.os as OSAgendar) ?? null);
  }

  function onDragMove(e: DragMoveEvent) {
    const ativador = e.activatorEvent as PointerEvent | undefined;
    if (!ativador) return;
    realcar(celulaEm(ativador.clientX + e.delta.x, ativador.clientY + e.delta.y));
  }

  /**
   * O FullCalendar não expõe as células como alvos do dnd-kit, então descobrimos
   * o dia pela posição final do ponteiro.
   *
   * `elementsFromPoint` (plural) e não `elementFromPoint`: o DragOverlay do dnd-kit
   * NÃO define `pointer-events:none` no próprio wrapper (conferido na lib), então a
   * versão singular devolve o overlay — que está sob o cursor o tempo todo — e o dia
   * nunca era encontrado.
   */
  function onDragEnd(e: DragEndEvent) {
    const os = e.active.data.current?.os as OSAgendar | undefined;
    setArrastando(null);
    realcar(null);
    if (!os) return;

    const ativador = e.activatorEvent as PointerEvent | undefined;
    if (!ativador) return;

    const cel = celulaEm(ativador.clientX + e.delta.x, ativador.clientY + e.delta.y);
    const iso = cel?.getAttribute('data-date');
    if (iso) abrirAgendar(os, iso);
  }

  function onDragCancel() {
    setArrastando(null);
    realcar(null);
  }

  async function agendar() {
    if (!formNova.data_inicio) {
      toast.error('Informe a data.');
      return;
    }
    setAgendando(true);
    try {
      // Quem vai é definido aqui, no ato de agendar (pré-marcado com a equipe
      // ativa; a pessoa ajusta a exceção). Salva junto — sem passo extra.
      await api.post('/agenda/visitas', {
        os_id: formNova.os_id,
        data_inicio: formNova.data_inicio,
        data_fim: formNova.data_fim || null,
        hora: formNova.hora || null,
        instaladores: formNova.instaladores,
        status: formNova.status,
        pauta: formNova.pauta || null,
      });
      setFormNova(VAZIO);
      await Promise.all([carregarVisitas(), carregarOsAgendar()]); // sem spinner: nao remonta o calendario
      toast.success('Visita(s) agendada(s).');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao agendar.');
    } finally {
      setAgendando(false);
    }
  }

  /** Abre o form de novo evento avulso num dia (pelo "+" no topo da célula). */
  function abrirNovoEvento(data: string) {
    setFormEvento({
      data_inicio: data,
      hora: '',
      tipo: 'lembrete',
      titulo: '',
      custo: 'a_definir',
      pauta: '',
      instaladores: [],
      status: 'agendada',
    });
  }

  /** Cria um evento avulso (sem OS). Exige título; hora é opcional. */
  async function criarEvento() {
    if (!formEvento) return;
    if (!formEvento.titulo.trim()) {
      toast.error('Escreva o título do evento.');
      return;
    }
    setCriandoEvento(true);
    try {
      await api.post('/agenda/visitas', {
        tipo: formEvento.tipo,
        titulo: formEvento.titulo.trim(),
        custo: formEvento.custo,
        data_inicio: formEvento.data_inicio,
        hora: formEvento.hora || null,
        instaladores: formEvento.instaladores,
        pauta: formEvento.pauta || null,
        status: formEvento.status,
      });
      setFormEvento(null);
      await carregarVisitas(); // sem spinner: nao remonta o calendario
      toast.success('Evento criado.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao criar evento.');
    } finally {
      setCriandoEvento(false);
    }
  }

  /** Resumo da OS: clicar no corpo de um evento traz o "resumão" (OSDetalhe).
   *  Recebe também a visita clicada — é a pauta DELA que se edita lá dentro. */
  async function abrirResumo(osId: number, visita: VisitaAgenda) {
    setCarregandoResumo(true);
    setResumoVisita(visita);
    setPautaEdit(null); // abre em leitura; o lápis é que libera o campo
    try {
      setResumoOs(await api.get<OSDetalhe>(`/ordens-servico/${osId}`));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao abrir a OS.');
    } finally {
      setCarregandoResumo(false);
    }
  }

  /** Resumão do evento AVULSO (lembrete/entrega/…): mesmo modal, sem os blocos
   *  da OS. O "O que fazer" fica no mesmo lugar que na visita de OS — era em
   *  outro fluxo (modal de edição inteiro) que o texto se perdia sem salvar. */
  function abrirResumoAvulso(visita: VisitaAgenda) {
    setResumoOs(null);
    setResumoVisita(visita);
    setPautaEdit(null);
  }

  /** Fecha o resumão. Se o "O que fazer" estava aberto com texto diferente do
   *  salvo, avisa — com um clique pra salvar — em vez de perder em silêncio
   *  (era a "observação que some" da Suany, 27/07). */
  function fecharResumo() {
    const v = resumoVisita;
    const texto = pautaEdit;
    setResumoOs(null);
    setResumoVisita(null);
    setPautaEdit(null);
    if (v?.id && texto !== null && texto.trim() !== (v.pauta ?? '').trim()) {
      toast.warning('O "O que fazer" não foi salvo.', {
        action: {
          label: 'Salvar',
          onClick: () =>
            salvarPautaDe(v, texto)
              .then(() => toast.success('Pauta salva — já sai assim no roteiro.'))
              .catch(() => toast.error('Falha ao salvar a pauta.')),
        },
      });
    }
  }

  /** O PUT da pauta em si — usado pelo Salvar do resumão e pelo "Salvar" do
   *  aviso de texto não salvo (quando o modal já fechou). */
  async function salvarPautaDe(v: VisitaAgenda, texto: string) {
    await api.put(`/agenda/visitas/${v.id}`, {
      data: v.data || null,
      hora: v.hora || null,
      instaladores: v.instaladores.map((x) => x.id),
      status: v.status,
      pauta: texto,
    });
    await carregarVisitas(); // sem spinner: nao remonta o calendario
  }

  /**
   * Grava a pauta editada no resumão. Manda só o campo (mais `data`/`hora`, que o
   * PUT precisa pra não desmarcar o dia) — o resto é pegajoso no backend e fica
   * como está. String vazia limpa de propósito.
   */
  async function salvarPauta() {
    const v = resumoVisita;
    if (!v?.id || pautaEdit === null) return;
    setSalvandoPauta(true);
    try {
      await salvarPautaDe(v, pautaEdit);
      setResumoVisita({ ...v, pauta: pautaEdit || null });
      setPautaEdit(null);
      toast.success('Pauta salva — já sai assim no roteiro.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar a pauta.');
    } finally {
      setSalvandoPauta(false);
    }
  }

  /**
   * Duplica uma visita: cria outra pra mesma OS, **sem hora** (o Vittorio quer
   * evitar colisão de horário) mas **no mesmo dia**. A data fica de propósito: uma
   * visita sem data não aparece no calendário e não haveria como arrastá-la — o
   * combinado é "duplicar e arrastar pro dia", então a cópia precisa nascer visível.
   * Reusa o POST /visitas (a criação normal), sem endpoint novo.
   */
  async function duplicarVisita(v: VisitaAgenda) {
    try {
      const criadas = await api.post<OSVisita[]>('/agenda/visitas', {
        os_id: v.os_id,
        data_inicio: v.data,
        data_fim: null,
        hora: null,
        instaladores: v.instaladores.map((x) => x.id),
        status: v.status,
        prioridade: v.prioridade ?? 'normal',
        motivo: v.motivo ?? null,
        // A pauta vem junto só como ponto de partida: a cópia quase sempre é um
        // retorno ("finalizar o que ficou"), e o texto muda. Por isso a cópia já
        // abre no editor — sem isso, o roteiro do novo dia sairia com o texto do
        // dia anterior, que foi exatamente a queixa da Suany.
        pauta: v.pauta || null,
      });
      await carregarVisitas();
      const nova = criadas?.[0];
      if (nova?.id) setEditando({ ...v, id: nova.id, hora: null, dia_inteiro: true });
      toast.success('Cópia criada no mesmo dia — ajuste a pauta e arraste pro dia certo.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao duplicar.');
    }
  }

  /** Pede a exclusão: abre o modal de confirmação do tema (não o confirm nativo). */
  function excluirVisita(v: VisitaAgenda) {
    if (v.id) setVisitaExcluir(v);
  }

  /** Confirma a exclusão de fato — chamado pelo botão "Remover" do modal. */
  async function confirmarExcluir() {
    const v = visitaExcluir;
    if (!v?.id) return;
    setExcluindo(true);
    try {
      await api.delete(`/agenda/visitas/${v.id}`);
      await Promise.all([carregarVisitas(), carregarOsAgendar()]);
      setVisitaExcluir(null);
      toast.success('Agendamento excluído.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao excluir.');
    } finally {
      setExcluindo(false);
    }
  }

  function alternarExpandir(id: number) {
    setExpandidos((s) => {
      const novo = new Set(s);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  /**
   * Cadeado: "já terminei de agendar esta OS". Manual e reversível — NÃO se deduz
   * de ter visita marcada (Vittorio, 17/07: uma OS pode ter 2, 3 visitas e seguir
   * em montagem). É só pra enxergar o que já foi resolvido enquanto se monta.
   */
  const reordenarPorCadeado = (lista: OSAgendar[]) =>
    // Sort estável (só pela flag) — trancadas pro fim, ordem preservada em cada grupo.
    [...lista].sort(
      (a, b) => Number(a.agendamento_concluido) - Number(b.agendamento_concluido),
    );

  async function alternarCadeado(o: OSAgendar) {
    const novo = !o.agendamento_concluido;

    // 1) Troca a flag na hora (o cadeado muda e o cartão esmaece), mas NÃO reordena
    //    ainda — deixa a animação "saindo" rodar primeiro.
    setOsAgendar((lista) =>
      lista.map((x) => (x.id === o.id ? { ...x, agendamento_concluido: novo } : x)),
    );

    // 2) Anima em duas fases pra o olho pegar: desliza pra fora → reordena → pulsa
    //    no novo lugar. (Se destrancar, o mesmo: some da base da fila e reaparece
    //    mais acima.)
    setMovimento({ id: o.id, fase: 'saindo' });
    window.setTimeout(() => {
      setOsAgendar(reordenarPorCadeado);
      setMovimento({ id: o.id, fase: 'chegando' });
      window.setTimeout(() => {
        setMovimento((m) => (m?.id === o.id ? null : m));
      }, 700);
    }, 420);

    // 3) Servidor em paralelo; se falhar, ressincroniza (flag e ordem).
    try {
      await api.put(`/agenda/visitas/os/${o.id}/agendamento-concluido`, { concluido: novo });
    } catch (err) {
      setMovimento(null);
      await carregarOsAgendar();
      toast.error(err instanceof ApiError ? err.message : 'Falha ao mudar o cadeado.');
    }
  }

  /**
   * Remarcar não recarrega a lista: o FullCalendar já pôs o evento no lugar e o
   * estado local acompanha. Recarregar aqui era o que fazia o calendário piscar —
   * e é desperdício, já sabemos o resultado. Se o servidor recusar, `reverter()`
   * (do próprio FullCalendar) devolve o evento pro dia de origem.
   */
  async function reagendar(
    visita: VisitaAgenda,
    novaData: string,
    reverter: () => void,
  ) {
    const dataAnterior = visita.data;
    // Soltou o card num dia FORA do período da viagem vinculada → o vínculo cai
    // junto (senão o evento continua saindo no roteiro da viagem — o card
    // "gruda e leva a viagem"). Sair é consequência do arrasto; entrar numa
    // viagem continua sendo ato manual (o carrinho do card).
    const vg = visita.viagem_id
      ? viagens.find((x) => x.id === visita.viagem_id)
      : undefined;
    const fimViagem = vg ? vg.data_chegada || vg.data_partida : null;
    const saiDaViagem =
      !!vg &&
      !!vg.data_partida &&
      !!fimViagem &&
      (novaData < vg.data_partida || novaData > fimViagem);
    setVisitas((vs) =>
      vs.map((v) =>
        v.id === visita.id
          ? { ...v, data: novaData, ...(saiDaViagem ? { viagem_id: null } : {}) }
          : v,
      ),
    );
    try {
      await api.put(`/agenda/visitas/${visita.id}`, {
        // Remarcar reenvia a classificação atual pra ela não se perder (o backend
        // preserva o que vier null, mas mandar o valor é mais explícito).
        tipo: visita.tipo ?? null,
        titulo: visita.titulo ?? null,
        custo: visita.custo ?? null,
        data: novaData,
        hora: visita.hora || null,
        instaladores: visita.instaladores.map((x) => x.id),
        status: visita.status,
        prioridade: visita.prioridade ?? 'normal',
        motivo: visita.motivo ?? null,
        ...(saiDaViagem ? { viagem_id: 0 } : {}),
      });
      if (saiDaViagem && vg) {
        toast(`Saiu da viagem ${nomeViagem(vg)}.`, {
          action: {
            label: 'Desfazer',
            onClick: () =>
              definirViagemDoEvento(
                { ...visita, data: novaData, viagem_id: null },
                vg.id,
              ),
          },
        });
      }
    } catch (err) {
      setVisitas((vs) =>
        vs.map((v) =>
          v.id === visita.id
            ? { ...v, data: dataAnterior, viagem_id: visita.viagem_id ?? null }
            : v,
        ),
      );
      reverter();
      toast.error(err instanceof ApiError ? err.message : 'Falha ao remarcar.');
    }
  }

  /** O PUT do "editar tudo" — usado pelo Salvar do modal e pelo "Salvar" do
   *  aviso de alterações não salvas (quando o modal já fechou). */
  async function salvarEditDe(ed: VisitaAgenda) {
    await api.put(`/agenda/visitas/${ed.id}`, {
      tipo: ed.tipo ?? null,
      titulo: ed.titulo ?? null, // só o avulso usa; visita de OS manda null
      custo: ed.custo ?? null,
      data: ed.data || null,
      hora: ed.hora || null,
      instaladores: ed.instaladores.map((x) => x.id),
      status: ed.status,
      prioridade: ed.prioridade ?? 'normal',
      motivo: ed.motivo ?? null,
      // '' (e não null) porque aqui a pessoa PODE ter apagado o texto: no
      // backend null = "não mexe" (pegajoso) e '' = limpa.
      pauta: ed.pauta ?? '',
      desfecho: ed.desfecho ?? '',
      // Endereço próprio: só o avulso edita (visita de OS usa o da OS e nem
      // mostra os campos — não manda pra não sujar).
      ...(ed.os_id
        ? {}
        : {
            endereco: ed.endereco ?? '',
            numero: ed.numero ?? '',
            complemento: ed.complemento ?? '',
            bairro: ed.bairro ?? '',
            cidade: ed.cidade ?? '',
            uf: ed.uf ?? '',
            cep: ed.cep ?? '',
          }),
    });
  }

  async function salvarEdit() {
    if (!editando?.id) return;
    setSalvandoEdit(true);
    try {
      await salvarEditDe(editando);
      setEditando(null);
      await carregarVisitas(); // sem spinner: nao remonta o calendario
      toast.success('Visita salva.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.');
    } finally {
      setSalvandoEdit(false);
    }
  }

  /** Fecha o "editar tudo" (X, backdrop ou Cancelar). Mudou algo e não salvou?
   *  Avisa — com um clique pra salvar — em vez de perder em silêncio (era a
   *  "observação que some" da Suany: escrever e fechar sem o Salvar do rodapé). */
  function fecharEdit() {
    const ed = editando;
    setEditando(null);
    if (!ed?.id) return;
    const orig = visitas.find((x) => x.id === ed.id);
    if (!orig || !editSujo(ed, orig)) return;
    toast.warning('Alterações não salvas.', {
      action: {
        label: 'Salvar',
        onClick: () =>
          salvarEditDe(ed)
            .then(async () => {
              await carregarVisitas();
              toast.success('Visita salva.');
            })
            .catch(() => toast.error('Falha ao salvar.')),
      },
    });
  }

  // Auto-save de "quem vai" direto no card da agenda (na parte expandida) — sem
  // abrir modal nem ir na OS. Grava na hora, na mesma visita.
  async function toggleInstaladorNaAgenda(v: VisitaAgenda, inst: Instalador) {
    if (!v.id) return;
    const tem = v.instaladores.some((x) => x.id === inst.id);
    const novos = tem
      ? v.instaladores.filter((x) => x.id !== inst.id)
      : [...v.instaladores, inst];
    setVisitas((arr) =>
      arr.map((x) => (x.id === v.id ? { ...x, instaladores: novos } : x)),
    ); // otimista: reflete já no card
    try {
      await api.put(`/agenda/visitas/${v.id}`, {
        tipo: v.tipo ?? null,
        titulo: v.titulo ?? null,
        custo: v.custo ?? null,
        data: v.data || null,
        hora: v.hora || null,
        instaladores: novos.map((x) => x.id),
        status: v.status,
        prioridade: v.prioridade ?? 'normal',
        motivo: v.motivo ?? null,
      });
      await carregarVisitas();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.');
      await carregarVisitas(); // reverte pro estado do servidor
    }
  }

  // Urgência (prioridade) direto no card — toggle otimista, mesma linguagem do
  // "quem vai"/viagem. Reenvia o payload inteiro (só o vínculo é pegajoso).
  async function definirPrioridade(v: VisitaAgenda, prioridade: PrioridadeVisita) {
    if (!v.id) return;
    setVisitas((arr) => arr.map((x) => (x.id === v.id ? { ...x, prioridade } : x)));
    try {
      await api.put(`/agenda/visitas/${v.id}`, {
        tipo: v.tipo ?? null,
        titulo: v.titulo ?? null,
        custo: v.custo ?? null,
        data: v.data || null,
        hora: v.hora || null,
        instaladores: v.instaladores.map((x) => x.id),
        status: v.status,
        prioridade,
        motivo: v.motivo ?? null,
        // pauta/desfecho não vão: PUT parcial não fala deles, o backend preserva.
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.');
      await carregarVisitas(); // só no erro: volta pro estado do servidor
    }
  }

  // Resultado da visita (realizada / não realizada) direto no card. Clicar o que já
  // está aceso volta pra "agendada". Motivo zera fora de "não realizada".
  async function definirResultado(v: VisitaAgenda, status: OSVisita['status']) {
    if (!v.id) return;
    const motivo = status === 'nao_realizada' ? v.motivo ?? null : null;
    setVisitas((arr) =>
      arr.map((x) => (x.id === v.id ? { ...x, status, motivo } : x)),
    );
    try {
      await api.put(`/agenda/visitas/${v.id}`, {
        tipo: v.tipo ?? null,
        titulo: v.titulo ?? null,
        custo: v.custo ?? null,
        data: v.data || null,
        hora: v.hora || null,
        instaladores: v.instaladores.map((x) => x.id),
        status,
        prioridade: v.prioridade ?? 'normal',
        motivo,
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.');
      await carregarVisitas(); // só no erro: volta pro estado do servidor
    }
  }

  // Desarquivar a OS a partir do card travado na agenda (Fatia 5b). Destrava os
  // eventos dela e recarrega agenda + painel.
  async function desarquivarOs(osId: number) {
    try {
      await api.post(`/ordens-servico/${osId}/desarquivar`, {});
      await Promise.all([carregarVisitas(), carregarOsAgendar()]);
      toast.success('OS desarquivada.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao desarquivar.');
    }
  }

  // Viagens (operacionais) que cobrem um dia — marca o dia no header e habilita o
  // carrinho no card. Excluir a viagem cancelada de propósito.
  function viagensNoDia(dataIso: string | null | undefined): ViagemLite[] {
    if (!dataIso) return [];
    return viagens.filter((vg) => {
      if (vg.status === 'cancelada' || !vg.data_partida) return false;
      const fim = vg.data_chegada || vg.data_partida;
      return dataIso >= vg.data_partida && dataIso <= fim;
    });
  }

  // Destino da viagem à qual o evento já está vinculado — texto do chip do card.
  function nomeDaViagem(id: number | null | undefined): string | null {
    const vg = viagens.find((x) => x.id === id);
    return vg ? nomeViagem(vg) : null;
  }

  /**
   * Liga (viagemId > 0) ou desliga (0) o evento numa viagem. Reenvia o payload
   * INTEIRO de propósito: no PUT só os campos de vínculo/classificação são
   * pegajosos — data/hora/status, se omitidos, seriam resetados.
   */
  async function definirViagemDoEvento(v: VisitaAgenda, viagemId: number) {
    if (!v.id) return;
    setVisitas((arr) =>
      arr.map((x) =>
        x.id === v.id ? { ...x, viagem_id: viagemId === 0 ? null : viagemId } : x,
      ),
    ); // otimista: reflete já no card
    try {
      await api.put(`/agenda/visitas/${v.id}`, {
        tipo: v.tipo ?? null,
        titulo: v.titulo ?? null,
        custo: v.custo ?? null,
        data: v.data || null,
        hora: v.hora || null,
        instaladores: v.instaladores.map((x) => x.id),
        status: v.status,
        viagem_id: viagemId,
      });
      await carregarVisitas();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.');
      await carregarVisitas(); // reverte pro estado do servidor
    }
  }

  /**
   * Clique no carrinho: se o evento já está numa viagem, sai dela; senão entra na
   * viagem do dia (se só 1) ou abre o seletor (se 2+ cobrem o dia).
   */
  function toggleViagemDoEvento(v: VisitaAgenda, e: ReactMouseEvent) {
    if (v.viagem_id) {
      definirViagemDoEvento(v, 0);
      return;
    }
    const vgs = viagensNoDia(v.data);
    if (vgs.length === 1) definirViagemDoEvento(v, vgs[0].id);
    else if (vgs.length > 1) {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setEscolhendoViagem({
        evento: v,
        left: r.left,
        top: r.top,
        bottom: r.bottom,
      });
    }
  }

  async function removerEdit() {
    if (!editando?.id) return;
    try {
      await api.delete(`/agenda/visitas/${editando.id}`);
      setEditando(null);
      await Promise.all([carregarVisitas(), carregarOsAgendar()]); // sem spinner: nao remonta o calendario
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao remover.');
    }
  }

  const osNova = osAgendar.find((o) => o.id === formNova.os_id);
  const osEmFoco = osAgendar.find((o) => o.id === osFoco);

  /* Quantos eventos cada dia tem — serve pra saber em QUAIS dias mostrar a
     setinha de recolher (só nos que estavam cortados, senão vira poluição). */
  const qtdPorDia = new Map<string, number>();
  for (const v of visitasFiltradas)
    if (v.data) qtdPorDia.set(v.data, (qtdPorDia.get(v.data) ?? 0) + 1);

  /**
   * Como está o dia — o que alimenta a pílula "Livre".
   *
   * Vocabulário do Vittorio (31/07): **livre é propriedade do dia, não um
   * evento**. O dia segue livre de instalação mesmo tendo médico, exame ou
   * reunião marcados; e uma instalação CANCELADA devolve o dia à condição de
   * livre — foi o que a Suany anotou à mão ("instalações canceladas") e que aqui
   * o sistema deduz sozinho.
   *
   * Olha as visitas SEM o filtro de status da tela: esconder as canceladas é uma
   * escolha de vista, não pode virar "o dia está vazio".
   */
  const diaTemInstalacao = new Set<string>();
  const diaTemAlgo = new Set<string>();
  const diaTemDiaSemOs = new Set<string>();
  for (const v of visitas) {
    if (!v.data || (loja && v.loja_codigo !== loja)) continue;
    if (v.status === 'cancelada') continue; // cancelado não ocupa o dia
    if (v.tipo === 'dia_sem_os') {
      diaTemDiaSemOs.add(v.data);
      continue; // é o anúncio de que não há trabalho — não conta como compromisso
    }
    diaTemAlgo.add(v.data);
    if (v.os_id) diaTemInstalacao.add(v.data);
  }

  const eventos = visitasFiltradas.map((v) => ({
    id: String(v.id),
    title: v.cliente_nome ?? v.os_codigo ?? '—',
    start: v.data ?? undefined,
    allDay: true,
    // Ordena o dia por horário; sem hora vai pro fim (Vittorio, 17/07).
    ordem: v.hora || '99:99',
    extendedProps: { visita: v },
  }));

  /* O "recolher" nasce como um EVENTO do próprio calendário, no fim do dia — é
     assim que ele cai exatamente onde estava o "+N ⌄" (a volta pelo mesmo lugar
     da ida). Só nos dias que estavam cortados; `ordem` alta joga pro fim, e ele
     não é arrastável nem clicável como visita (ver os ramos `recolher` nos
     handlers do calendário). */
  if (expandirDias)
    for (const [dia, qtd] of qtdPorDia)
      if (qtd > MAX_EVENTOS_DIA)
        eventos.push({
          id: `recolher-${dia}`,
          title: '',
          start: dia,
          allDay: true,
          ordem: 'zzzz',
          editable: false,
          extendedProps: { recolher: true },
        } as unknown as (typeof eventos)[number]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <style>{CSS_LOCAL}</style>

      <CustomMolduraPagina
        titulo="Agendamentos"
        descricao="Marque as instalações. Búzios e Trancoso são agendas distintas — escolha a loja para montar a sua."
        largura="cheia"
        semCaminho
        acoes={
          <button
            type="button"
            className="btn btn-outline-theme btn-sm"
            onClick={alternarTelaCheia}
            title="Ver a agenda em tela cheia"
          >
            <i className="bi bi-arrows-fullscreen me-1"></i> Tela cheia
          </button>
        }
      >

        {/* Filtro de loja — Búzios e Trancoso são agendas distintas. */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="small text-secondary me-1">
            <i className="bi bi-shop me-1"></i> Agenda de:
          </span>
          <div className="btn-group btn-group-sm" role="group">
            {LOJAS.map((l) => (
              <button
                key={l.codigo}
                type="button"
                className={`btn ${loja === l.codigo ? 'btn-theme' : 'btn-outline-theme'}`}
                onClick={() => {
                  setLoja(l.codigo);
                  setOsFoco(null);
                }}
              >
                {l.nome}
                <span className="badge badge-light text-bg-light ms-1">
                  {l.codigo
                    ? osAgendar.filter((o) => o.loja_codigo === l.codigo).length
                    : osAgendar.length}
                </span>
              </button>
            ))}
          </div>
          {!loja && (
            <span className="small text-secondary">
              <i className="bi bi-info-circle me-1"></i>
              Búzios e Trancoso misturados — escolha uma loja pra montar a agenda.
            </span>
          )}
        </div>

        {/* Filtro do calendário por status do evento + urgência (Fatia 5a): mostra
            ou esconde os cards; NÃO apaga nada, só oculta da vista. */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="small text-secondary me-1">
            <i className="bi bi-funnel me-1"></i> Mostrar:
          </span>
          <div className="btn-group btn-group-sm" role="group">
            {STATUS_VISITA.map((s) => {
              const on = statusVisiveis.has(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  className={`btn ${on ? 'btn-theme' : 'btn-outline-theme'}`}
                  onClick={() => toggleStatusVisivel(s.value)}
                  title={
                    on
                      ? `Esconder ${s.label.toLowerCase()}`
                      : `Mostrar ${s.label.toLowerCase()}`
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className={`btn btn-sm ${soUrgentes ? 'btn-theme' : 'btn-outline-theme'}`}
            onClick={() => setSoUrgentes((x) => !x)}
            title="Mostrar só as urgentes"
          >
            <i className="bi bi-circle-fill text-danger me-1"></i> Só urgentes
          </button>
        </div>

        {/* Área que vai pra tela cheia: faixa + calendário. Em fullscreen o
            navegador mostra só este elemento, então ele precisa do próprio fundo
            (o `bg-body`) e de rolagem própria — senão fica preto e sem scroll. O
            DragOverlay e os modais também moram aqui dentro (ver adiante), senão
            somem em tela cheia. */}
        <div
          ref={areaRef}
          className={telaCheia ? 'bg-body p-3 overflow-auto' : ''}
          style={telaCheia ? { height: '100vh' } : undefined}
        >
          {/* Alvo do portal dos seletores de data/hora (CustomCamposData): fica DENTRO da
              área da agenda pra o popup do react-datepicker não ser cortado pelo
              modal e continuar visível no modo Tela Cheia. */}
          <div id={DP_PORTAL_ID} />
          {telaCheia && (
            <div className="d-flex justify-content-end mb-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-theme"
                onClick={alternarTelaCheia}
              >
                <i className="bi bi-fullscreen-exit me-1"></i> Sair da tela cheia
                (Esc)
              </button>
            </div>
          )}

        {/* ── Faixa de OS a agendar ────────────────────────────────────────── */}
        <div className="card adminuiux-card mb-3">
          <div className="card-body pb-2">
            <div className="row gx-3 align-items-center mb-3">
              <div className="col">
                <h6 className="mb-0">
                  <i className="bi bi-clipboard-check text-theme-1 me-1"></i> OS a
                  agendar{' '}
                  <span className="badge badge-light text-bg-theme-1">
                    {osFiltradas.length}
                  </span>
                </h6>
                <p className="text-secondary small mb-0">
                  Arraste pela <strong>barrinha</strong>{' '}
                  <i className="bi bi-grip-vertical"></i> até um dia — ou clique no
                  cartão pra ver onde ela já está marcada.
                </p>
              </div>
              {/* Busca instantânea: filtra a faixa a cada tecla. */}
              <div className="col-12 col-md-auto mt-2 mt-md-0">
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    style={{ minWidth: '14rem' }}
                    placeholder="Buscar OS, cliente ou pedido…"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                  {busca && (
                    <button
                      type="button"
                      className="btn btn-outline-theme"
                      onClick={() => setBusca('')}
                      title="Limpar busca"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-auto mt-2 mt-md-0">
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${estiloCard === 'cartao' ? 'btn-theme' : 'btn-outline-theme'}`}
                    onClick={() => setEstiloCard('cartao')}
                  >
                    <i className="bi bi-credit-card-2-front me-1"></i> Cartão
                  </button>
                  <button
                    type="button"
                    className={`btn ${estiloCard === 'detalhado' ? 'btn-theme' : 'btn-outline-theme'}`}
                    onClick={() => setEstiloCard('detalhado')}
                  >
                    <i className="bi bi-card-text me-1"></i> Detalhado
                  </button>
                </div>
              </div>
            </div>

            {/* Filtro do painel (Fatia 5b): reexibe concluídas / arquivadas (default
                mostra só o que falta agendar). Elas vêm no fim da lista, marcadas. */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-secondary me-1">
                <i className="bi bi-funnel me-1"></i> Também mostrar:
              </span>
              <button
                type="button"
                className={`btn btn-sm ${incluirConcluidas ? 'btn-theme' : 'btn-outline-theme'}`}
                onClick={() => setIncluirConcluidas((x) => !x)}
                title="Reexibir OS concluídas (pra mexer em algo)"
              >
                <i className="bi bi-check2-circle me-1"></i> Concluídas
              </button>
              <button
                type="button"
                className={`btn btn-sm ${incluirArquivadas ? 'btn-theme' : 'btn-outline-theme'}`}
                onClick={() => setIncluirArquivadas((x) => !x)}
                title="Reexibir OS arquivadas (pra reabrir)"
              >
                <i className="bi bi-archive me-1"></i> Arquivadas
              </button>
            </div>

            {/* OS em foco: conta o que está fora do mês à vista, que o calendário
                sozinho não tem como mostrar. */}
            {osEmFoco && (
              <div className="alert alert-primary d-flex flex-wrap align-items-center gap-2 py-2 mb-3">
                <i className="bi bi-eye"></i>
                <span className="small">
                  Mostrando as datas de <strong>{osEmFoco.codigo}</strong> —{' '}
                  {datasDaOsFoco.length === 0
                    ? 'ainda sem visita marcada.'
                    : `${datasDaOsFoco.length} visita(s).`}
                  {/* "fora da vista" e não "fora do mês": na semana e na lista o
                      período exibido não é um mês. */}
                  {datasForaDaVista.length > 0 && (
                    <>
                      {' '}
                      <strong>
                        {datasForaDaVista.length} fora do período exibido:{' '}
                        {datasForaDaVista.map((d) => formatarDiaMes(d)).join(', ')}
                      </strong>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link ms-auto py-0"
                  onClick={() => setOsFoco(null)}
                >
                  Limpar
                </button>
              </div>
            )}

            {osFiltradas.length === 0 ? (
              <p className="text-secondary small py-3 mb-0">
                {busca ? (
                  <>
                    Nenhuma OS bate com <strong>“{busca}”</strong>
                    {loja ? ` em ${LOJAS.find((l) => l.codigo === loja)?.nome}` : ''}.{' '}
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => setBusca('')}
                    >
                      Limpar busca
                    </button>
                  </>
                ) : (
                  <>
                    Nenhuma OS pendente de agendamento
                    {loja ? ` em ${LOJAS.find((l) => l.codigo === loja)?.nome}` : ''}.
                  </>
                )}
              </p>
            ) : (
              <div className="overflow-hidden">
                <Swiper
                  /* pt-2 tanto quanto pb-2: o wrapper é `overflow-hidden`, então
                     sem folga em cima o contorno de destaque do cartão (outline de
                     3px) era cortado no topo — aparecia só nas laterais e na base. */
                  className="overflow-visible pt-2 pb-2"
                  slidesPerView="auto"
                  spaceBetween={16}
                  noSwiping
                  noSwipingClass="no-swipe"
                >
                  {osFiltradas.map((o) => (
                    <SwiperSlide className="w-auto" key={o.id}>
                      {estiloCard === 'cartao' ? (
                        <OsCartao
                          o={o}
                          emFoco={osFoco === o.id}
                          movimento={movimento?.id === o.id ? movimento.fase : null}
                          onClick={() => focarOs(o.id)}
                          onCadeado={() => alternarCadeado(o)}
                          onAbrirOS={() => abrirOS(o.id)}
                        />
                      ) : (
                        <OsCard
                          o={o}
                          onClick={() => focarOs(o.id)}
                        />
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>
        </div>

        {/* ── Calendário ───────────────────────────────────────────────────── */}
        <div className="card adminuiux-card">
          <div className={`card-body${piscaHoje ? ' pisca-hoje' : ''}`}>
            {/* Experimento reversível (22/07): alterna o visual do card no calendário.
                Default = Barra (decisão 25/07); Clássico continua disponível. */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-secondary me-1">
                <i className="bi bi-palette me-1"></i>Aparência:
              </span>
              <div className="btn-group btn-group-sm" role="group" aria-label="Estilo do card">
                <button
                  type="button"
                  className={`btn ${estiloEvento === 'barra' ? 'btn-theme' : 'btn-outline-theme'}`}
                  onClick={() => setEstiloEvento('barra')}
                >
                  Barra
                </button>
                <button
                  type="button"
                  className={`btn ${estiloEvento === 'classico' ? 'btn-theme' : 'btn-outline-theme'}`}
                  onClick={() => setEstiloEvento('classico')}
                >
                  Clássico
                </button>
              </div>
              <div className="btn-group btn-group-sm" role="group" aria-label="Título do card">
                <button
                  type="button"
                  className={`btn ${tituloCard === 'codigo' ? 'btn-theme' : 'btn-outline-theme'}`}
                  onClick={() => setTituloCard('codigo')}
                >
                  Título: OS
                </button>
                <button
                  type="button"
                  className={`btn ${tituloCard === 'cliente' ? 'btn-theme' : 'btn-outline-theme'}`}
                  onClick={() => setTituloCard('cliente')}
                >
                  Título: Cliente
                </button>
              </div>
            </div>
            {carregando ? (
              <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
                <span className="spinner-border spinner-border-sm" /> Carregando…
              </div>
            ) : (
              <FullCalendar
                ref={calendarioRef}
                plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
                initialView="cincoSemanas"
                locale={ptBr}
                height="auto"
                weekends={fimDeSemana}
                events={eventos}
                editable
                eventStartEditable
                eventOrder="ordem"
                dayMaxEvents={expandirDias ? false : MAX_EVENTOS_DIA}
                /* "+2 mais" vira "+2" com uma seta pra baixo: o clique não abre
                   camada nenhuma, ele ESTICA o dia. A seta é o que promete isso. */
                moreLinkContent={(arg) => (
                  <span className="d-inline-flex align-items-center gap-1">
                    +{arg.num}
                    <i className="bi bi-chevron-down"></i>
                  </span>
                )}
                /* ATENÇÃO ao retorno. O FullCalendar decide assim (internal-common.js):
                     if (!moreLinkClick || moreLinkClick === 'popover') → abre o popover
                     else if (typeof moreLinkClick === 'string')        → navega pra essa view
                   Ou seja: função que **não retorna nada** cai no primeiro caso e o
                   popover abre do mesmo jeito (foi o que aconteceu na 1ª versão).
                   Devolvendo um valor truthy que NÃO é string, ele não faz nada — que
                   é o que queremos, porque quem expande é o nosso estado. O tipo
                   público só admite string, daí o cast. */
                moreLinkClick={
                  ((): unknown => {
                    setExpandirDias(true);
                    return true;
                  }) as unknown as MoreLinkAction
                }
                headerToolbar={{
                  /* O botão fica SEMPRE na barra (como o Seg–Sex), trocando só o
                     rótulo: expandido, o "+N" some da grade e o caminho de volta
                     não pode depender de um botão que aparece e some. */
                  left: 'irHoje prev,next fimDeSemana expandirDias',
                  center: 'title',
                  right: 'cincoSemanas,dayGridMonth,dayGridWeek,listWeek,listDay',
                }}
                /* Fim de semana é ajuste do calendário, então mora na barra dele.
                   `customButtons` é o jeito do próprio FullCalendar de pôr um botão
                   nosso na toolbar — nada de enfiar um switch solto na página. */
                customButtons={{
                  /* Botão próprio (o `today` nativo desabilita sozinho — ver irParaHoje). */
                  irHoje: {
                    text: 'Hoje',
                    click: irParaHoje,
                  },
                  fimDeSemana: {
                    text: fimDeSemana ? 'Seg–Dom' : 'Seg–Sex',
                    click: () => setFimDeSemana((v) => !v),
                  },
                  expandirDias: {
                    text: expandirDias ? 'Recolher dias' : 'Expandir dias',
                    click: () => setExpandirDias((v) => !v),
                  },
                }}
                /**
                 * "5 semanas": visão própria (recurso nativo do FullCalendar, sem
                 * gambiarra). Diferente do Mês, que mostra um mês do calendário e
                 * joga a semana atual no meio da tela, o `dayGrid` com duração em
                 * semanas **começa sempre na semana da data atual** — daí a semana
                 * corrente cair na 1ª linha e o resto ser o futuro próximo, que é
                 * como se monta agenda (dá-se a virada de mês de lambuja).
                 */
                views={{
                  cincoSemanas: {
                    type: 'dayGrid',
                    duration: { weeks: 5 },
                    /* Sem isto, prev/next pula 5 SEMANAS de uma vez (a duração da
                       visão) — o "vai pra quando não faz sentido". Com incremento de
                       1 semana, as setas deslizam a janela uma semana por clique. */
                    dateIncrement: { weeks: 1 },
                    buttonText: '5 semanas',
                  },
                  /* "Dia": a lista de um dia só — o roteiro daquele dia, que é o que
                     a Suany imprime. `listDay` é nativo; só o rótulo é nosso. */
                  listDay: { buttonText: 'Dia' },
                  listWeek: { buttonText: 'Lista' },
                }}
                datesSet={(arg) => {
                  const de = arg.startStr.slice(0, 10);
                  const ate = arg.endStr.slice(0, 10); // exclusivo (dia seguinte ao último)
                  const tipo = arg.view.type;
                  setVista({ de, ate, tipo });

                  // Trocou de VISÃO (não só navegou)? Reancora no dia em foco de antes
                  // da troca — a nova visão passa a mostrar o período que o contém. O
                  // gotoDate dispara outro datesSet, mas com o MESMO tipo, então não
                  // reentra por aqui (sem laço).
                  const trocouView =
                    tipoViewRef.current !== null && tipoViewRef.current !== tipo;
                  tipoViewRef.current = tipo;
                  if (trocouView) {
                    arg.view.calendar.gotoDate(focoRef.current);
                    return;
                  }

                  // Navegação normal (prev/next/Hoje): recalcula o dia em foco. Se hoje
                  // está à vista, o foco é hoje. Senão, `currentStart` — o dia 1º no
                  // Mês, o início da semana na Semana — e NÃO o início da grade, senão
                  // o Mês ancoraria num dia do mês anterior ao trocar pra Semana.
                  const hoje = hojeISO();
                  focoRef.current =
                    hoje >= de && hoje < ate
                      ? hoje
                      : paraIsoLocal(arg.view.currentStart);
                }}
                dayCellClassNames={(arg) => {
                  const iso = paraIsoLocal(arg.date);
                  const classes: string[] = [];
                  if (datasDaOsFoco.includes(iso)) classes.push('dia-da-os');
                  if (iso >= semanaAtual.de && iso <= semanaAtual.ate)
                    classes.push('semana-atual');
                  if (abreMesNaTela(arg.date)) classes.push('primeiro-do-mes');
                  /* Só na "5 semanas": no Mês o próprio FullCalendar já marca os
                     dias de fora. Reusamos a classe DELE (`fc-day-other`) em vez de
                     inventar uma nossa — assim o cinza sai idêntico ao do Mês, com o
                     estilo que o tema já define em _calendar.scss:440. Uma classe
                     nossa perderia em especificidade e simplesmente não pintava. */
                  if (
                    vista?.tipo === 'cincoSemanas' &&
                    mesPrincipal &&
                    iso.slice(0, 7) !== mesPrincipal
                  )
                    classes.push('fc-day-other');
                  return classes;
                }}
                /* Onde o mês vira, escreve "3 ago" em vez de só "3" — sem isso a
                   troca de mês passa batido numa grade de 5 semanas seguidas. */
                /* Topo do dia: "+" à esquerda (cria evento avulso ali) e o número à
                   direita. Renderizado pelo próprio React do FullCalendar (nada de
                   mexer no DOM dele à mão — foi o que deu tela branca antes). */
                dayCellContent={(arg) => (
                  <span className="dia-topo">
                    <span className="dia-topo-esq d-inline-flex align-items-center gap-1">
                      <span
                        className="btn-novo-evento"
                        role="button"
                        title="Novo evento neste dia"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirNovoEvento(paraIsoLocal(arg.date));
                        }}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </span>
                      <span
                        className="btn-novo-evento js-acao-dia"
                        role="button"
                        title="Imprimir roteiro deste dia"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agenda/roteiro?data=${paraIsoLocal(arg.date)}`);
                        }}
                      >
                        <i className="bi bi-printer"></i>
                      </span>
                      {/* MARCAR O QUE ELES VEEM — junto do "+" e da impressora,
                          porque é ação DO DIA. Olho = os cards deste dia;
                          calendário = os cards da semana toda. Três aparências
                          (nenhum / alguns / todos) e duas ações: completa ou
                          zera. Some quando não há card: não há o que marcar. */}
                      {(() => {
                        const iso = paraIsoLocal(arg.date);
                        const semana = inicioSemana(iso);
                        const doDia = estadoLiberacao(iso, iso);
                        const daSemana = estadoLiberacao(semana, addDias(semana, 6));
                        const marca = (e: 'vazio' | 'parcial' | 'cheio') =>
                          e === 'cheio'
                            ? ' liberado-cheio'
                            : e === 'parcial'
                              ? ' liberado-parcial'
                              : '';
                        return (
                          <>
                            {contarMarcados(iso, iso).total > 0 && (
                              <span
                                className={`btn-novo-evento js-acao-dia${marca(doDia)}`}
                                role="button"
                                title={
                                  doDia === 'cheio'
                                    ? 'Os instaladores veem tudo deste dia — clique para esconder'
                                    : doDia === 'parcial'
                                      ? 'Parte do dia está visível — clique para mostrar tudo'
                                      : 'Mostrar este dia para os instaladores'
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  alternarEmLote('dia', iso);
                                }}
                              >
                                <i
                                  className={`bi bi-${
                                    doDia === 'cheio'
                                      ? 'eye-fill'
                                      : doDia === 'parcial'
                                        ? 'eye'
                                        : 'eye-slash'
                                  }`}
                                ></i>
                              </span>
                            )}
                            {contarMarcados(semana, addDias(semana, 6)).total > 0 && (
                              <span
                                className={`btn-novo-evento js-acao-dia${marca(daSemana)}`}
                                role="button"
                                title={
                                  daSemana === 'cheio'
                                    ? 'A semana inteira está visível — clique para esconder'
                                    : daSemana === 'parcial'
                                      ? 'Parte da semana está visível — clique para mostrar tudo'
                                      : 'Mostrar a SEMANA inteira para os instaladores'
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  alternarEmLote('semana', iso);
                                }}
                              >
                                {/* SÓLIDO só quando a semana INTEIRA está
                                    visível. Antes o parcial também vinha
                                    preenchido, e marcar um único dia acendia o
                                    calendário de todos os dias da semana, como
                                    se ela estivesse liberada (Vittorio, 09/08).
                                    Parcial fica em contorno colorido — o mesmo
                                    degrau que o olho já faz. */}
                                <i
                                  className={`bi bi-${
                                    daSemana === 'cheio'
                                      ? 'calendar-week-fill'
                                      : 'calendar-week'
                                  }`}
                                ></i>
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </span>
                    {/* Coluna do meio. Existe SEMPRE, mesmo sem pílula nenhuma:
                        é o slot vazio que segura o número do dia na direita.
                        Sem ele, num dia sem viagem e sem "Livre" o número
                        escorregaria para o centro. */}
                    <span className="dia-topo-meio">
                    {viagensNoDia(paraIsoLocal(arg.date)).length > 0 && (
                      <span
                        className="badge rounded-pill bg-theme-1 fw-medium d-inline-flex align-items-center gap-1 flex-shrink-1 overflow-hidden lh-sm js-acao-dia"
                        role="button"
                        /* O roteiro por extenso vem no title porque a pílula
                           mostra o nome CURTO — é aqui que "Rio" volta a ser
                           "Rio de Janeiro → Niterói" para quem precisa. */
                        title={
                          viagensNoDia(paraIsoLocal(arg.date)).length === 1
                            ? `${roteiroDaViagem(viagensNoDia(paraIsoLocal(arg.date))[0])} — clique para imprimir o roteiro`
                            : 'Imprimir roteiro (do dia ou da viagem toda)'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          const r = (
                            e.currentTarget as HTMLElement
                          ).getBoundingClientRect();
                          setSpeed({
                            left: r.left,
                            top: r.top,
                            bottom: r.bottom,
                            dia: paraIsoLocal(arg.date),
                          });
                        }}
                      >
                        <i className="bi bi-signpost-2"></i>
                        <span className="text-truncate">
                          {viagensNoDia(paraIsoLocal(arg.date)).length === 1
                            ? nomeViagem(viagensNoDia(paraIsoLocal(arg.date))[0])
                            : `${viagensNoDia(paraIsoLocal(arg.date)).length} viagens`}
                        </span>
                      </span>
                    )}
                    {/* "Livre": propriedade do dia, não evento. Só onde ela ainda
                        pode agendar — daqui pra frente, em dia útil, e quando não
                        há viagem (aí a pílula da viagem já dá o contexto do dia). */}
                    {(() => {
                      const iso = paraIsoLocal(arg.date);
                      const diaSemana = arg.date.getDay();

                      if (
                        iso < hojeISO() ||
                        diaSemana === 0 ||
                        diaSemana === 6 ||
                        viagensNoDia(iso).length > 0 ||
                        diaTemInstalacao.has(iso) ||
                        // Já existe o card "Dia sem OS" no dia dizendo isso — a
                        // pílula seria a mesma frase duas vezes (Vittorio, 31/07).
                        diaTemDiaSemOs.has(iso)
                      )
                        return null;
                      const soLivre = !diaTemAlgo.has(iso);
                      return (
                        <span
                          className={`pilula-livre rounded-pill lh-sm flex-shrink-1 overflow-hidden${
                            soLivre ? '' : ' pilula-livre-parcial'
                          }`}
                          title={
                            soLivre
                              ? 'Dia sem nenhum compromisso'
                              : 'Tem compromisso, mas nenhuma instalação'
                          }
                        >
                          <span className="text-truncate">
                            {soLivre ? 'Livre' : 'Livre de instalações'}
                          </span>
                        </span>
                      );
                    })()}
                    </span>
                    <span className="dia-topo-dir">
                    {abreMesNaTela(arg.date) ? (
                      <span className="fw-bold">
                        {arg.date.getDate()}{' '}
                        {arg.date.toLocaleDateString('pt-BR', { month: 'short' })}
                      </span>
                    ) : arg.isToday ? (
                      /* Círculo do dia atual = componente .avatar do tema, não CSS próprio.
                         `getDate()`, e NÃO `dayNumberText`: o FullCalendar escreve o mês
                         por extenso ("9 de agosto") no primeiro dia VISÍVEL da grade, que
                         nem sempre é o dia 1º — e o `abreMesNaTela` acima, que trata a
                         virada de mês, não pega esse caso. O texto longo estourava o
                         círculo de 30px (que tem overflow:hidden) e virava uma bola azul
                         com o número ilegível. Visto com o fim de semana ligado, em
                         09/08/2026: a grade começava no domingo, que era hoje. */
                      <span className="avatar avatar-30 rounded-circle bg-theme-1">
                        {arg.date.getDate()}
                      </span>
                    ) : (
                      <span>{arg.dayNumberText}</span>
                    )}
                    </span>
                  </span>
                )}
                /* Cor do evento = tipo de serviço, com as classes de evento do
                   tema. Quando a OS está em foco, troca pelo destaque forte do
                   AdminUX (`bg-theme-1-space text-white`, o mesmo que a demo
                   Calendar.tsx usa) — aí o evento salta de verdade. */
                eventClassNames={(arg) => {
                  // O "recolher" não é visita: sem fundo, sem borda, sem cor de tipo.
                  if (arg.event.extendedProps.recolher) return ['evento-recolher'];
                  const v = arg.event.extendedProps.visita as VisitaAgenda;
                  if (osFoco && v.os_id === osFoco)
                    return ['bg-theme-1-space', 'text-white', 'evento-da-os'];
                  /* "Dia sem OS" vem antes de qualquer estilo: a hachura vale nos
                     dois visuais (Barra e Clássico), porque o que ela diz não é
                     "de que categoria é" e sim "isto não é tarefa". */
                  const hachura = v.tipo === 'dia_sem_os' ? ['evento-dia-sem-os'] : [];
                  // Estilo "Barra": fundo por CATEGORIA (OS x avulso) + a COR do tipo na
                  // borda esquerda, via classe (reativa ao toggle). Sem "salada" de corpo.
                  if (estiloEvento === 'barra')
                    return [
                      'evento-barra',
                      `evento-barra-${corBsDoEvento(v)}`,
                      ...(!v.os_id ? ['evento-barra-avulso'] : []),
                      ...hachura,
                    ];
                  // Clássico (default): cor no corpo, como está hoje.
                  if (!v.os_id) {
                    const t = (v.tipo ?? 'lembrete') as Exclude<TipoEvento, 'os'>;
                    const info = TIPO_EVENTO[t] ?? TIPO_EVENTO.lembrete;
                    return [info.evento, 'evento-avulso', ...hachura];
                  }
                  return [v.os_tipo ? TIPO_INFO[v.os_tipo].evento : 'bg-secondary-subtle'];
                }}
                eventContent={(arg) => {
                  /* Fecha o dia de onde ele foi aberto: mesma linha do "+N ⌄",
                     agora apontando pra cima. */
                  if (arg.event.extendedProps.recolher)
                    return (
                      <span
                        className="link-recolher d-inline-flex align-items-center gap-1"
                        role="button"
                        title="Recolher — volta o calendário à altura normal"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandirDias(false);
                        }}
                      >
                        <i className="bi bi-chevron-up"></i> recolher
                      </span>
                    );
                  const v = arg.event.extendedProps.visita as VisitaAgenda;
                  if (!v.os_id)
                    return (
                      <EventoAvulso
                        v={v}
                        estilo={estiloEvento}
                        viagensDoDia={viagensNoDia(v.data)}
                        naViagem={v.viagem_id != null}
                        viagemNome={nomeDaViagem(v.viagem_id)}
                        onToggleViagem={(e) => toggleViagemDoEvento(v, e)}
                        onToggleLiberacao={() => alternarLiberacaoCard(v)}
                        onEditar={() => setEditando(v)}
                        onExcluir={() => excluirVisita(v)}
                      />
                    );
                  return (
                    <EventoVisita
                      v={v}
                      estilo={estiloEvento}
                      heroi={tituloCard}
                      expandido={v.id != null && expandidos.has(v.id)}
                      emFoco={osFoco != null && v.os_id === osFoco}
                      instaladoresOpcoes={instaladores}
                      viagensDoDia={viagensNoDia(v.data)}
                      naViagem={v.viagem_id != null}
                      viagemNome={nomeDaViagem(v.viagem_id)}
                      onToggleViagem={(e) => toggleViagemDoEvento(v, e)}
                      onToggleInstalador={(inst) => toggleInstaladorNaAgenda(v, inst)}
                      onToggleUrgencia={() =>
                        definirPrioridade(v, v.prioridade === 'alta' ? 'normal' : 'alta')
                      }
                      onDefinirResultado={(status) => definirResultado(v, status)}
                      onToggleLiberacao={() => alternarLiberacaoCard(v)}
                      onDesarquivar={() => v.os_id && desarquivarOs(v.os_id)}
                      onEditarHora={() => setEditando(v)}
                      onDuplicar={() => duplicarVisita(v)}
                      onExcluir={() => excluirVisita(v)}
                      onFocarOs={() => v.os_id && focarOs(v.os_id)}
                      onToggleExpandir={() => v.id != null && alternarExpandir(v.id)}
                    />
                  );
                }}
                /* Clique no corpo do evento = resumão, COM ou SEM OS. O lembrete
                   abre o mesmo resumo (com menos blocos) — o "O que fazer" fica no
                   mesmo lugar, com o mesmo lápis, nos dois mundos. O modal grande
                   virou o "Editar tudo" (botão no rodapé do resumão).
                   As ações internas chamam stopPropagation, então não disparam aqui. */
                eventClick={(arg) => {
                  if (arg.event.extendedProps.recolher) {
                    setExpandirDias(false); // clicar em qualquer canto da linha recolhe
                    return;
                  }
                  const v = arg.event.extendedProps.visita as VisitaAgenda;
                  if (v.os_arquivada) return; // travada: só o botão Desarquivar age
                  if (v.os_id) abrirResumo(v.os_id, v);
                  else abrirResumoAvulso(v);
                }}
                eventDrop={(arg) =>
                  reagendar(
                    arg.event.extendedProps.visita as VisitaAgenda,
                    arg.event.startStr.slice(0, 10),
                    arg.revert,
                  )
                }
                /* Clique num dia: com OS em foco, agenda ela ali (atalho pra quem
                   não quer arrastar); sem foco, cria um evento avulso no dia. As DUAS
                   formas convivem — o "+" no topo (dayCellContent) e este clique. */
                dateClick={(arg) => {
                  // Cliques nos botões do cabeçalho do dia (🖨 / pílula de viagem)
                  // têm ação própria — o stopPropagation do React não segura o
                  // dateClick do FullCalendar, então filtramos pelo alvo aqui.
                  const alvo = arg.jsEvent?.target as HTMLElement | undefined;
                  if (alvo?.closest('.js-acao-dia')) return;
                  if (osEmFoco) abrirAgendar(osEmFoco, arg.dateStr);
                  else abrirNovoEvento(arg.dateStr);
                }}
              />
            )}
          </div>
        </div>

          {/* Sombra do card enquanto arrasta. Mora DENTRO da área de tela cheia:
              o DragOverlay renderiza com position:fixed no lugar da árvore onde
              está, e em fullscreen só o que é descendente do elemento cheio
              aparece — se ficasse fora, o fantasma sumia ao arrastar em tela cheia. */}
          <DragOverlay>
            {arrastando && (
              <div
                className={`card adminuiux-card width-250 shadow ${TIPO_INFO[arrastando.tipo].tema} bg-theme-r-gradient`}
                style={{ pointerEvents: 'none' }}
              >
                <div className="card-body py-2">
                  <p className="mb-0 fw-medium">{arrastando.codigo}</p>
                  <p className="small mb-0 opacity-75 text-truncated">
                    {arrastando.cliente_nome ?? '—'}
                  </p>
                </div>
              </div>
            )}
          </DragOverlay>
        </div>
      </CustomMolduraPagina>

      {/* Escolher a viagem quando 2+ cobrem o dia (BZ e TR): speed dial no carrinho. */}
      {escolhendoViagem && (
        <SpeedDial
          left={escolhendoViagem.left}
          top={escolhendoViagem.top}
          bottom={escolhendoViagem.bottom}
          container={telaCheia ? areaRef.current : null}
          onClose={() => setEscolhendoViagem(null)}
          acoes={viagensNoDia(escolhendoViagem.evento.data).map((vg) => ({
            icon: 'bi-car-front',
            label: `Incluir · ${nomeViagem(vg)}`,
            onClick: () => definirViagemDoEvento(escolhendoViagem.evento, vg.id),
          }))}
        />
      )}

      {/* Speed dial de impressão do roteiro (sai da pílula de viagem do dia). */}
      {speed && (
        <SpeedDial
          left={speed.left}
          top={speed.top}
          bottom={speed.bottom}
          container={telaCheia ? areaRef.current : null}
          onClose={() => setSpeed(null)}
          acoes={[
            {
              icon: 'bi-calendar-day',
              label: 'Roteiro deste dia',
              onClick: () => navigate(`/agenda/roteiro?data=${speed.dia}`),
            },
            ...viagensNoDia(speed.dia).map((vg) => ({
              icon: 'bi-signpost-2',
              label: `Viagem toda · ${nomeViagem(vg)}`,
              onClick: () => navigate(`/agenda/roteiro?viagem=${vg.id}`),
            })),
          ]}
        />
      )}

      {/* Modal: agendar */}
      {/* container: em tela cheia o modal precisa renderizar DENTRO da área cheia,
          senão o portal joga ele no body (fora do fullscreen) e ele fica invisível
          — e arrastar uma OS pro dia abre justamente este modal. */}
      <Modal
        show={!!formNova.os_id}
        onHide={() => setFormNova(VAZIO)}
        centered
        container={telaCheia ? areaRef.current : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>Agendar visita — {osNova?.codigo}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <p className="small text-secondary mb-0">
            {osNova?.cliente_nome}
            {osNova?.pedido ? ` · Ped. ${osNova.pedido}` : ''}
            {osNova?.endereco_resumo ? ` · ${osNova.endereco_resumo}` : ''}
          </p>
          <div className="row g-3">
            <div className="col-6">
              <label className="form-label">Data</label>
              <CustomCampoData
                valor={formNova.data_inicio}
                onChange={(iso) => setFormNova((f) => ({ ...f, data_inicio: iso }))}
              />
            </div>
            <div className="col-6">
              <label className="form-label">Hora (opcional)</label>
              <CustomCampoHora
                valor={formNova.hora}
                onChange={(hhmm) => setFormNova((f) => ({ ...f, hora: hhmm }))}
              />
            </div>
            <div className="col-6">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formNova.status}
                onChange={(e) =>
                  setFormNova((f) => ({
                    ...f,
                    status: e.target.value as OSVisita['status'],
                  }))
                }
              >
                {STATUS_VISITA.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">O que fazer nesta visita</label>
            <input
              className="form-control"
              placeholder="Ex.: instalar só os trilhos da suíte"
              value={formNova.pauta}
              onChange={(e) => setFormNova((f) => ({ ...f, pauta: e.target.value }))}
            />
            <div className="aviso-roteiro mt-1">
              <i className="bi bi-printer"></i>
              Sai no roteiro impresso — e dá pra mudar depois, clicando no card.
            </div>
          </div>
          <div>
            <label className="form-label">Quem vai</label>
            <ChipsInstaladores
              opcoes={instaladores}
              selecionados={formNova.instaladores}
              onToggle={(id) =>
                setFormNova((f) => ({
                  ...f,
                  instaladores: f.instaladores.includes(id)
                    ? f.instaladores.filter((x) => x !== id)
                    : [...f.instaladores, id],
                }))
              }
            />
          </div>
          <p className="small text-secondary mb-0">
            A equipe já vem marcada — desmarque quem não vai. Pra vários dias, use o
            duplicar no card.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setFormNova(VAZIO)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-theme"
            onClick={agendar}
            disabled={agendando}
          >
            {agendando && <span className="spinner-border spinner-border-sm me-1" />}
            Agendar
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal: novo evento avulso (pelo "+" no topo de um dia) */}
      <Modal
        show={!!formEvento}
        onHide={() => setFormEvento(null)}
        centered
        container={telaCheia ? areaRef.current : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-pin-angle me-2"></i>Novo evento
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <div>
            <label className="form-label">Tipo</label>
            <SeletorTipoEvento
              valor={formEvento?.tipo ?? 'lembrete'}
              onSelecionar={(t) => setFormEvento((f) => f && { ...f, tipo: t })}
            />
          </div>
          <div>
            <label className="form-label">Título</label>
            <input
              className="form-control"
              autoFocus
              placeholder="Ex.: Entrega de material no cliente"
              value={formEvento?.titulo ?? ''}
              onChange={(e) =>
                setFormEvento((f) => f && { ...f, titulo: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') criarEvento();
              }}
            />
          </div>
          <div>
            <label className="form-label">O que fazer (opcional)</label>
            <input
              className="form-control"
              value={formEvento?.pauta ?? ''}
              onChange={(e) =>
                setFormEvento((f) => f && { ...f, pauta: e.target.value })
              }
            />
            <div className="aviso-roteiro mt-1">
              <i className="bi bi-printer"></i> Sai no roteiro impresso.
            </div>
          </div>
          <div className="row g-3">
            <div className="col-4">
              <label className="form-label">Data</label>
              <CustomCampoData
                valor={formEvento?.data_inicio ?? ''}
                onChange={(iso) =>
                  setFormEvento((f) => f && { ...f, data_inicio: iso })
                }
              />
            </div>
            <div className="col-4">
              <label className="form-label">Hora (opcional)</label>
              <CustomCampoHora
                valor={formEvento?.hora ?? ''}
                onChange={(hhmm) => setFormEvento((f) => f && { ...f, hora: hhmm })}
              />
            </div>
            <div className="col-4">
              <label className="form-label">Gera custo?</label>
              <select
                className="form-select"
                value={formEvento?.custo ?? 'a_definir'}
                onChange={(e) =>
                  setFormEvento(
                    (f) => f && { ...f, custo: e.target.value as CustoEvento },
                  )
                }
              >
                {CUSTO_OPCOES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Pessoas envolvidas (opcional)</label>
            <ChipsInstaladores
              opcoes={instaladores}
              selecionados={formEvento?.instaladores ?? []}
              onToggle={(id) =>
                setFormEvento((f) => {
                  if (!f) return f;
                  const tem = f.instaladores.includes(id);
                  return {
                    ...f,
                    instaladores: tem
                      ? f.instaladores.filter((x) => x !== id)
                      : [...f.instaladores, id],
                  };
                })
              }
            />
          </div>
          <p className="small text-secondary mb-0">
            Um evento avulso não está ligado a nenhuma OS — serve pra registrar o que o
            dia tem (entrega, retirada, serviço na loja, folga, um recado).
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setFormEvento(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-theme"
            onClick={criarEvento}
            disabled={criandoEvento}
          >
            {criandoEvento && (
              <span className="spinner-border spinner-border-sm me-1" />
            )}
            Criar evento
          </button>
        </Modal.Footer>
      </Modal>

      {/* Modal: editar visita ("editar tudo"). Largo (lg) — o endereço do evento
          avulso vai entrar aqui — e com floating labels (o padrão do tema): o
          label dentro do campo economiza a linha do rótulo. */}
      <Modal
        show={!!editando}
        onHide={fecharEdit}
        centered
        size="lg"
        dialogClassName="modal-evento"
        container={telaCheia ? areaRef.current : undefined}
      >
        {editando && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                {editando.os_id ? `Visita — ${editando.os_codigo}` : 'Evento'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {editando.os_id && (
                <button
                  type="button"
                  onClick={() => editando.os_id && abrirOS(editando.os_id)}
                  className="btn btn-link p-0 text-decoration-none mb-3"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i> Abrir OS{' '}
                  {editando.os_codigo}
                </button>
              )}
              <AbasEvento
                avulso={!editando.os_id}
                endereco={
                  <div className="row gx-3">
                    <div className="col-12 col-md-8 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-rua"
                          placeholder="Endereço (rua)"
                          value={editando.endereco ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) => v && { ...v, endereco: e.target.value },
                            )
                          }
                        />
                        <label htmlFor="ev-rua">Endereço (rua)</label>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-numero"
                          placeholder="Número"
                          value={editando.numero ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) => v && { ...v, numero: e.target.value },
                            )
                          }
                        />
                        <label htmlFor="ev-numero">Número</label>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-complemento"
                          placeholder="Complemento"
                          value={editando.complemento ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) => v && { ...v, complemento: e.target.value },
                            )
                          }
                        />
                        <label htmlFor="ev-complemento">Complemento</label>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-bairro"
                          placeholder="Bairro"
                          value={editando.bairro ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) => v && { ...v, bairro: e.target.value },
                            )
                          }
                        />
                        <label htmlFor="ev-bairro">Bairro</label>
                      </div>
                    </div>
                    <div className="col-6 col-md-4 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-cidade"
                          placeholder="Cidade"
                          value={editando.cidade ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) => v && { ...v, cidade: e.target.value },
                            )
                          }
                        />
                        <label htmlFor="ev-cidade">Cidade</label>
                      </div>
                    </div>
                    <div className="col-4 col-md-2 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-uf"
                          placeholder="UF"
                          maxLength={2}
                          value={editando.uf ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) =>
                                v && { ...v, uf: e.target.value.toUpperCase() },
                            )
                          }
                        />
                        <label htmlFor="ev-uf">UF</label>
                      </div>
                    </div>
                    <div className="col-8 col-md-4 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-cep"
                          placeholder="CEP"
                          value={editando.cep ?? ''}
                          onChange={(e) =>
                            setEditando((v) => v && { ...v, cep: e.target.value })
                          }
                        />
                        <label htmlFor="ev-cep">CEP</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <div
                        className="form-text"
                        style={{ paddingLeft: '0.75rem' }}
                      >
                        <i className="bi bi-signpost me-1"></i> O roteiro agrupa
                        as paradas por bairro e cidade.
                      </div>
                    </div>
                  </div>
                }
              >
              <div className="row gx-3">
                {!editando.os_id && (
                  <>
                    <div className="col-12 col-md-8 mb-3">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="ev-titulo"
                          placeholder="Título"
                          value={editando.titulo ?? ''}
                          onChange={(e) =>
                            setEditando(
                              (v) => v && { ...v, titulo: e.target.value },
                            )
                          }
                        />
                        <label htmlFor="ev-titulo">Título</label>
                      </div>
                    </div>
                    <div className="col-12 col-md-4 mb-3">
                      <div className="form-floating">
                        <select
                          className="form-select"
                          id="ev-tipo"
                          value={
                            (editando.tipo ?? 'lembrete') as Exclude<
                              TipoEvento,
                              'os'
                            >
                          }
                          onChange={(e) =>
                            setEditando(
                              (v) =>
                                v && {
                                  ...v,
                                  tipo: e.target.value as TipoEvento,
                                },
                            )
                          }
                        >
                          {TIPOS_EVENTO.map((t) => (
                            <option key={t} value={t}>
                              {TIPO_EVENTO[t].label}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="ev-tipo">Tipo</label>
                      </div>
                    </div>
                  </>
                )}
                <div className="col-6 col-md-3 mb-3">
                  <CustomCampoData
                    id="ev-data"
                    label="Data"
                    valor={editando.data ?? ''}
                    onChange={(iso) => setEditando((v) => v && { ...v, data: iso })}
                  />
                </div>
                <div className="col-6 col-md-3 mb-3">
                  <CustomCampoHora
                    id="ev-hora"
                    label="Hora"
                    valor={editando.hora ?? ''}
                    onChange={(hhmm) => setEditando((v) => v && { ...v, hora: hhmm })}
                  />
                </div>
                <div className="col-6 col-md-3 mb-3">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="ev-status"
                      value={editando.status}
                      onChange={(e) =>
                        setEditando((v) => {
                          if (!v) return v;
                          const status = e.target.value as OSVisita['status'];
                          // Motivo só vale com "Não realizada" — limpa nos outros.
                          return {
                            ...v,
                            status,
                            motivo: status === 'nao_realizada' ? v.motivo : null,
                          };
                        })
                      }
                    >
                      {STATUS_VISITA.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="ev-status">Status</label>
                  </div>
                </div>
                <div className="col-6 col-md-3 mb-3">
                  <div className="form-floating">
                    <select
                      className="form-select"
                      id="ev-prioridade"
                      value={editando.prioridade ?? 'normal'}
                      onChange={(e) =>
                        setEditando(
                          (v) =>
                            v && {
                              ...v,
                              prioridade: e.target.value as PrioridadeVisita,
                            },
                        )
                      }
                    >
                      {PRIORIDADES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="ev-prioridade">Prioridade</label>
                  </div>
                </div>
                {!editando.os_id && (
                  <div className="col-6 col-md-3 mb-3">
                    <div className="form-floating">
                      <select
                        className="form-select"
                        id="ev-custo"
                        value={editando.custo ?? 'a_definir'}
                        onChange={(e) =>
                          setEditando(
                            (v) =>
                              v && { ...v, custo: e.target.value as CustoEvento },
                          )
                        }
                      >
                        {CUSTO_OPCOES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="ev-custo">Gera custo?</label>
                    </div>
                  </div>
                )}
                {editando.status === 'nao_realizada' && (
                  <div className="col-12 col-md-6 mb-3">
                    <div className="form-floating">
                      <select
                        className="form-select"
                        id="ev-motivo"
                        value={editando.motivo ?? ''}
                        onChange={(e) =>
                          setEditando(
                            (v) =>
                              v && {
                                ...v,
                                motivo: (e.target.value ||
                                  null) as OSVisita['motivo'],
                              },
                          )
                        }
                      >
                        <option value="">—</option>
                        {MOTIVOS_NAO_REALIZADA.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="ev-motivo">Motivo de não ter sido feito</label>
                    </div>
                  </div>
                )}
                {/* Divide a linha com o "Gera custo?" (avulso) — os chips não
                    precisam da largura toda e sobrava uma linha meio vazia. */}
                <div
                  className={`col-12 mb-3 ${editando.os_id ? '' : 'col-md-9'}`}
                >
                  <p className="small text-secondary mb-1">
                    {editando.os_id ? 'Instaladores' : 'Pessoas envolvidas'}
                  </p>
                  <ChipsInstaladores
                    opcoes={instaladores}
                    selecionados={editando.instaladores.map((x) => x.id)}
                    onToggle={(id) =>
                      setEditando((v) => {
                        if (!v) return v;
                        const inst = instaladores.find((x) => x.id === id);
                        if (!inst) return v;
                        const tem = v.instaladores.some((x) => x.id === id);
                        return {
                          ...v,
                          instaladores: tem
                            ? v.instaladores.filter((x) => x.id !== id)
                            : [...v.instaladores, inst],
                        };
                      })
                    }
                  />
                </div>
                {/* Os dois textos da visita, separados: pauta é o que VAI ser feito
                    (e é o que o instalador lê no papel); desfecho é como terminou. */}
                <div className="col-12 mb-3">
                  {/* ACIMA do campo (comparado com a versão embaixo em 31/07, e o
                      Vittorio preferiu esta): lido antes de escrever, o aviso vira
                      contexto do que se vai digitar, e não uma nota de rodapé. */}
                  <span className="aviso-roteiro mb-1">
                    <i className="bi bi-printer"></i> Este texto sai no roteiro impresso
                  </span>
                  <CustomAutoTextarea
                    id="ev-pauta"
                    label="O que fazer nesta visita"
                    placeholder="Ex.: finalizar o quarto da empregada"
                    value={editando.pauta ?? ''}
                    onChange={(t) => setEditando((v) => v && { ...v, pauta: t })}
                  />
                </div>
                <div className="col-12">
                  {/* O par do aviso de cima: mesmo lugar, mesmo formato, muda a cor.
                      Um embaixo do outro, os dois campos se explicam sozinhos. */}
                  <span className="aviso-roteiro aviso-mudo mb-1">
                    <i className="bi bi-eye-slash"></i> Fica só aqui — não vai pro papel
                  </span>
                  <CustomAutoTextarea
                    id="ev-desfecho"
                    label="Como terminou (opcional)"
                    placeholder={
                      editando.status === 'nao_realizada'
                        ? 'Detalhe do motivo'
                        : 'Preenchido depois da visita'
                    }
                    value={editando.desfecho ?? ''}
                    onChange={(t) => setEditando((v) => v && { ...v, desfecho: t })}
                  />
                </div>
              </div>
              </AbasEvento>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
              <button
                type="button"
                className="btn btn-outline-theme text-danger"
                onClick={removerEdit}
              >
                <i className="bi bi-trash me-1"></i> Remover
              </button>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={fecharEdit}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-theme"
                  onClick={salvarEdit}
                  disabled={salvandoEdit}
                >
                  {salvandoEdit && (
                    <span className="spinner-border spinner-border-sm me-1" />
                  )}
                  Salvar
                </button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Modal do evento (clique no corpo do card): um só componente para a
          visita de OS e para o evento avulso — mesmo cabeçalho, mesmas abas,
          mesmo rodapé (com o Excluir dentro). Ver `ModalEvento`, no fim do arquivo. */}
      <ModalEvento
        carregando={carregandoResumo}
        os={resumoOs}
        visita={resumoVisita}
        container={telaCheia ? areaRef.current : undefined}
        viagemNome={nomeDaViagem(resumoVisita?.viagem_id)}
        pautaEdit={pautaEdit}
        setPautaEdit={setPautaEdit}
        salvandoPauta={salvandoPauta}
        onSalvarPauta={salvarPauta}
        onFechar={fecharResumo}
        onAbrirOs={() => resumoOs && abrirOS(resumoOs.id)}
        onEditar={() => {
          // Leva o texto em edição junto pro "editar tudo" — nada se perde e o
          // aviso de não-salvo não dispara à toa.
          const v = resumoVisita;
          if (!v) return;
          const texto = pautaEdit;
          setResumoOs(null);
          setResumoVisita(null);
          setPautaEdit(null);
          setEditando({ ...v, pauta: texto ?? v.pauta });
        }}
        onExcluir={() => {
          const v = resumoVisita;
          if (!v) return;
          // Fecha o resumo primeiro: quem confirma é o modal de exclusão, e dois
          // modais empilhados brigam pelo foco.
          setResumoOs(null);
          setResumoVisita(null);
          setPautaEdit(null);
          excluirVisita(v);
        }}
      />

      {/* Modal: confirmar exclusão — mesmo padrão das outras telas do app
          (despesas/custos), no lugar do window.confirm nativo. `container` pra
          funcionar também em tela cheia. */}
      <Modal
        show={visitaExcluir != null}
        onHide={() => setVisitaExcluir(null)}
        centered
        container={telaCheia ? areaRef.current : undefined}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">
            {visitaExcluir?.os_id ? 'Excluir agendamento' : 'Excluir evento'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {visitaExcluir?.os_id ? (
            <>
              Tem certeza que deseja excluir a visita
              {visitaExcluir?.data ? ` de ${formatarDiaMes(visitaExcluir.data)}` : ''} da
              OS <strong>{visitaExcluir?.os_codigo}</strong>
              {visitaExcluir?.cliente_nome ? ` (${visitaExcluir.cliente_nome})` : ''}?
              Esta ação não pode ser desfeita.
            </>
          ) : (
            <>
              Tem certeza que deseja excluir o evento{' '}
              <strong>{visitaExcluir?.titulo}</strong>
              {visitaExcluir?.data ? ` de ${formatarDiaMes(visitaExcluir.data)}` : ''}?
              Esta ação não pode ser desfeita.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setVisitaExcluir(null)}
          >
            Cancelar
          </button>
          {/* btn-theme puro, SEM text-danger. O tema usa a cor sólida do tema nas
              ações principais e reserva o vermelho pra acentos sutis (link de
              cancelar, ícone pequeno em tabela) — nunca letra vermelha sobre botão
              sólido, que briga com o tema azul/roxo. O caráter destrutivo já vem do
              título, do aviso e do ícone de lixeira. */}
          <button
            type="button"
            className="btn btn-theme"
            onClick={confirmarExcluir}
            disabled={excluindo}
          >
            {excluindo && <span className="spinner-border spinner-border-sm me-1" />}
            <i className="bi bi-trash me-1"></i> Excluir
          </button>
        </Modal.Footer>
      </Modal>

    </DndContext>
  );
}

// ── Subcomponentes ───────────────────────────────────────────────────────────

/**
 * Cartão de OS. Molde: `theme/adminux-react/src/pages/finance/FinanceBank.tsx:618` (o mostruário na RAIZ do projeto, fora do git) ("Credit cards
 * carousel"), sem a foto de fundo.
 *
 * Hierarquia pedida pelo Vittorio (17/07): **número da OS no topo em destaque**,
 * cliente logo abaixo, e o tipo desce pro rodapé ao lado do pedido — com ícone
 * pequeno, porque **quem diz o tipo é a cor do cartão**, não o ícone (o martelo
 * grande foi embora). Grip, OS, cliente e pedido alinhados na mesma margem.
 *
 * **Cadeado é acionável, não automático** (Vittorio, 17/07): quem fecha é quem está
 * montando a agenda, e reabre quando quiser. Ter visita marcada NÃO fecha nada — a OS
 * pode ter 2, 3 visitas e seguir em montagem. Fechada, ela esmaece e sai da frente,
 * mas continua arrastável: é sinal de gerência, não trava.
 * O **contador de visitas** é coisa separada, e esse sim é automático.
 */

/** Selo do card do painel (Fatia 5b): marca as REEXIBIDAS — arquivada (some por
 *  padrão, some via filtro "Arquivadas") ou concluída (via filtro "Concluídas"). */
function SeloOsAgendar({ o }: { o: OSAgendar }) {
  if (o.arquivada)
    return (
      <span className="badge text-bg-secondary align-middle">
        <i className="bi bi-archive me-1"></i>Arquivada
      </span>
    );
  if (o.status === 'concluida')
    return (
      <span className="badge bg-success-subtle text-success-emphasis align-middle">
        Concluída
      </span>
    );
  return null;
}

function OsCartao({
  o,
  emFoco,
  movimento,
  onClick,
  onCadeado,
  onAbrirOS,
}: {
  o: OSAgendar;
  emFoco: boolean;
  movimento: 'saindo' | 'chegando' | null;
  onClick: () => void;
  onCadeado: () => void;
  onAbrirOS: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({ id: `os:${o.id}`, data: { os: o } });
  const info = TIPO_INFO[o.tipo];
  const fechada = !!o.agendamento_concluido;
  const animacao =
    movimento === 'saindo'
      ? 'card-saindo'
      : movimento === 'chegando'
        ? 'card-chegando'
        : '';

  return (
    <div
      ref={setNodeRef}
      className={`card adminuiux-card width-250 ${info.tema} bg-theme-r-gradient overflow-hidden ${animacao} ${
        isDragging ? 'opacity-50' : ''
      }`}
      style={{
        opacity: fechada && !isDragging ? 0.55 : undefined,
        outline: emFoco ? '3px solid var(--bs-primary)' : undefined,
        outlineOffset: emFoco ? '1px' : undefined,
      }}
    >
      <div className="card-body position-relative z-index-1 p-3">
        {/* Topo: barrinha de arrastar + OS em destaque + cadeado */}
        <div className="row gx-2 align-items-center mb-3">
          <div className="col-auto">
            <span
              ref={setActivatorNodeRef}
              {...listeners}
              {...attributes}
              className="no-swipe d-inline-flex align-items-center justify-content-center rounded bg-white bg-opacity-25"
              style={{
                cursor: 'grab',
                touchAction: 'none',
                width: '1.5rem',
                height: '1.9rem',
              }}
              title="Arraste para um dia do calendário"
            >
              <i className="bi bi-grip-vertical"></i>
            </span>
          </div>
          <div className="col">
            {/* Clicar na OS abre a ordem de serviço e diz de onde veio, pra que o
                "Voltar" de lá devolva pra esta tela. `no-swipe` senão o Swiper
                engole o clique. */}
            <button
              type="button"
              onClick={onAbrirOS}
              className="no-swipe btn text-reset border-0 bg-transparent p-0 text-start"
              title={`Abrir a OS ${o.codigo}`}
            >
              <h5 className="mb-0 fw-medium">
                {o.codigo} <SeloOsAgendar o={o} />
              </h5>
            </button>
          </div>
          <div className="col-auto">
            {/* `no-swipe` aqui também: sem isso o Swiper come o clique. */}
            <button
              type="button"
              onClick={onCadeado}
              className={`no-swipe btn btn-sm text-reset border-0 p-1 lh-1 ${
                fechada ? 'bg-white bg-opacity-25' : ''
              }`}
              title={
                fechada
                  ? 'Agendamento fechado — clique pra reabrir'
                  : 'Marcar como já agendada'
              }
            >
              <i className={`bi ${fechada ? 'bi-lock-fill' : 'bi-unlock'}`}></i>
            </button>
          </div>
        </div>

        {/* Cliente — alinhado com a barrinha e com o pedido. fs-14 (e não h6): nome
            de cliente aqui é longo, e 16px truncava cedo demais. */}
        <button
          type="button"
          onClick={onClick}
          className="btn text-reset w-100 border-0 bg-transparent p-0 text-start"
        >
          <p className="fs-14 fw-medium mb-3 text-truncated">
            {nomeCliente(o.cliente_nome)}
          </p>
        </button>

        {/* Rodapé: pedido + tipo (ícone pequeno; a cor é que manda) */}
        <div className="row gx-2 align-items-end">
          <div className="col-auto fs-12">
            <p className="mb-0 opacity-50 small">Pedido</p>
            <p className="mb-0">{o.pedido || '—'}</p>
          </div>
          {/* Número de visitas volta pra cá (o cabeçalho estava bom sem ele). O
              destaque é só o número: cheio e em bold, com a palavra apagada atrás. */}
          <div className="col text-end fs-12">
            <p className="mb-0 small">
              {o.qtd_visitas > 0 ? (
                <>
                  <span className="fw-bold">{o.qtd_visitas}</span>{' '}
                  <span className="opacity-50">
                    {o.qtd_visitas === 1 ? 'visita' : 'visitas'}
                  </span>
                </>
              ) : (
                <span className="opacity-50">Sem visita</span>
              )}
            </p>
            <p className="mb-0 text-truncated">
              <i className={`bi ${info.icon} me-1`}></i>
              {rotuloTipo(o.tipo)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Card "detalhado" — o primeiro desenho, mantido pra comparação. */
function OsCard({ o, onClick }: { o: OSAgendar; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } =
    useDraggable({ id: `os:${o.id}`, data: { os: o } });
  const info = TIPO_INFO[o.tipo];

  return (
    <div
      ref={setNodeRef}
      /* A faixa lateral usa a cor que a classe `.theme-*` põe em
         --adminuiux-theme-1 — nada de hex aqui. */
      className={`card adminuiux-card width-250 ${info.tema} ${isDragging ? 'opacity-50' : ''}`}
      style={{ borderLeft: '4px solid var(--adminuiux-theme-1)' }}
    >
      <div className="card-header py-2">
        <div className="row gx-2 align-items-center">
          <div className="col-auto">
            <span
              ref={setActivatorNodeRef}
              {...listeners}
              {...attributes}
              className="no-swipe d-inline-flex align-items-center justify-content-center rounded text-secondary bg-body-secondary"
              style={{
                cursor: 'grab',
                touchAction: 'none',
                width: '1.75rem',
                height: '2rem',
                marginLeft: '-0.25rem',
              }}
              title="Arraste para um dia do calendário"
            >
              <i className="bi bi-grip-vertical"></i>
            </span>
          </div>
          <div className="col">
            <h6 className="mb-0">
              {o.codigo} <SeloOsAgendar o={o} />
            </h6>
          </div>
          <div className="col-auto">
            <span
              className={`badge badge-light ${
                o.qtd_visitas ? 'text-bg-secondary' : 'text-bg-warning'
              }`}
            >
              {o.qtd_visitas ? `${o.qtd_visitas} visita(s)` : 'sem visita'}
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onClick}
        className="card-body text-start border-0 bg-transparent py-2"
      >
        <p className="mb-1 fw-medium text-truncated">{nomeCliente(o.cliente_nome)}</p>
        <span className={`badge badge-light text-bg-theme-1 ${info.tema}`}>
          <i className={`bi ${info.icon} me-1`}></i>
          {rotuloTipo(o.tipo)}
        </span>
      </button>

      <div className="card-footer py-2">
        <div className="row gx-2 align-items-center">
          <div className="col-5">
            <p className="text-secondary small mb-0">
              <i className="bi bi-receipt"></i> Pedido
            </p>
            <p className="small mb-0 text-truncated">{o.pedido || '—'}</p>
          </div>
          <div className="col-7 text-end">
            <p className="text-secondary small mb-0">
              <i className="bi bi-geo-alt"></i> Local
            </p>
            <p className="small mb-0 text-truncated">{o.endereco_resumo || '—'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Evento no calendário (17/07 → reformulado 30/07).
 *
 * Cliques têm alvos diferentes, todos com stopPropagation pra não vazar pro
 * `eventClick` (que abre o resumo da OS):
 *  - corpo (cliente / bairro) → resumo da OS (é o `eventClick`, sem botão próprio).
 *  - relógio (topo direito) → tela da visita (reagendar / hora / status). Com hora:
 *    `🕐 09:00`. Sem hora: só o relógio discreto. Sem setinha, sem sublinhado.
 *  - rodapé → expandir (mostra mais linhas), duplicar, excluir — os três alinhados.
 *
 * Cor: os ícones de ação NÃO usam `btn-link` (que os deixava azuis). Herdam a cor
 * do texto do evento — preta no normal, branca no evento em foco. É "cor do sistema".
 * O tamanho dos três (relógio/hora + ícones) é fixado em `.evento-acao` (senão o
 * ícone bi sai a 16px e a hora a 12px, e desalinham).
 */
/**
 * Chip de viagem no card — liga/desliga o evento na viagem do dia.
 * Sólido na cor do tema quando está na viagem (mostra o DESTINO); outline
 * discreto quando fora. Substitui o iconezinho antigo (pequeno demais, 22/07).
 */
function ChipViagem({
  naViagem,
  destino,
  onToggle,
}: {
  naViagem: boolean;
  destino: string | null;
  onToggle: (e: ReactMouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(e);
      }}
      className={`badge rounded-pill d-inline-flex align-items-center gap-1 px-2 py-1 border lh-sm ${
        naViagem ? 'bg-theme-1' : 'bg-transparent text-theme-1'
      }`}
      style={{ maxWidth: '100%', borderColor: 'var(--adminuiux-theme-accent-1)' }}
      title={naViagem ? 'Tirar desta viagem' : 'Incluir na viagem do dia'}
    >
      <i className="bi bi-signpost-2"></i>
      <span className="text-truncate">
        {naViagem ? (destino ?? 'Na viagem') : 'incluir na viagem'}
      </span>
    </button>
  );
}

/** Marcadores do card (Fatia 5a): urgência (bolinha) + resultado (realizada / não
 *  realizada). Sempre visíveis, APAGADOS quando off (você vê o slot e confia no
 *  estado); clicáveis pra ligar/desligar na hora, sem abrir modal. Ficam numa linha
 *  só, à direita — na linha da viagem quando há viagem, senão na linha do endereço. */
function MarcadoresVisita({
  v,
  onToggleUrgencia,
  onDefinirResultado,
}: {
  v: VisitaAgenda;
  onToggleUrgencia: () => void;
  onDefinirResultado: (status: OSVisita['status']) => void;
}) {
  const parar = (e: ReactMouseEvent) => e.stopPropagation();
  const alta = v.prioridade === 'alta';
  const realizada = v.status === 'realizada';
  const naoRealizada = v.status === 'nao_realizada';
  // Off = cinza legível do tema (não some): o COLORIDO é que diz o que está ligado.
  const off = 'text-body-secondary';
  return (
    <div className="d-inline-flex align-items-center gap-1" onClick={parar}>
      <button
        type="button"
        className="evento-acao evento-botao lh-1 p-0"
        onClick={(e) => {
          parar(e);
          onToggleUrgencia();
        }}
        title={alta ? 'Urgência — clique p/ tirar' : 'Marcar urgência'}
      >
        <i
          className={`bi ${alta ? 'bi-circle-fill text-danger' : `bi-circle ${off}`}`}
        ></i>
      </button>
      <button
        type="button"
        className="evento-acao evento-botao lh-1 p-0"
        onClick={(e) => {
          parar(e);
          onDefinirResultado(realizada ? 'agendada' : 'realizada');
        }}
        title={realizada ? 'Realizada — clique p/ desfazer' : 'Marcar realizada'}
      >
        <i
          className={`bi ${realizada ? 'bi-check-circle-fill text-success' : `bi-check-circle ${off}`}`}
        ></i>
      </button>
      <button
        type="button"
        className="evento-acao evento-botao lh-1 p-0"
        onClick={(e) => {
          parar(e);
          onDefinirResultado(naoRealizada ? 'agendada' : 'nao_realizada');
        }}
        title={
          naoRealizada
            ? `Não realizada${v.motivo ? ' — ' + rotuloMotivo(v.motivo) : ''} — clique p/ desfazer`
            : 'Marcar não realizada'
        }
      >
        <i
          className={`bi ${naoRealizada ? 'bi-slash-circle-fill' : `bi-slash-circle ${off}`}`}
          style={
            naoRealizada ? { color: 'var(--bs-warning-text-emphasis)' } : undefined
          }
        ></i>
      </button>
    </div>
  );
}

function EventoVisita({
  v,
  estilo,
  heroi,
  expandido,
  emFoco,
  instaladoresOpcoes,
  viagensDoDia,
  naViagem,
  viagemNome,
  onToggleViagem,
  onToggleInstalador,
  onToggleUrgencia,
  onDefinirResultado,
  onToggleLiberacao,
  onDesarquivar,
  onEditarHora,
  onDuplicar,
  onExcluir,
  onFocarOs,
  onToggleExpandir,
}: {
  v: VisitaAgenda;
  estilo: 'classico' | 'barra';
  heroi: 'codigo' | 'cliente';
  expandido: boolean;
  emFoco: boolean;
  instaladoresOpcoes: Instalador[];
  viagensDoDia: ViagemLite[];
  naViagem: boolean;
  viagemNome: string | null;
  onToggleViagem: (e: ReactMouseEvent) => void;
  onToggleInstalador: (inst: Instalador) => void;
  onToggleUrgencia: () => void;
  onDefinirResultado: (status: OSVisita['status']) => void;
  onToggleLiberacao: () => void;
  onDesarquivar: () => void;
  onEditarHora: () => void;
  onDuplicar: () => void;
  onExcluir: () => void;
  onFocarOs: () => void;
  onToggleExpandir: () => void;
}) {
  const info = v.os_tipo ? TIPO_INFO[v.os_tipo] : null;
  const cancelada = v.status === 'cancelada';
  const hasViagem = viagensDoDia.length > 0;
  const parar = (e: ReactMouseEvent) => e.stopPropagation();
  // Código + sigla do tipo (INS/MAN/LAV) na cor da borda — no estilo Barra, é o que
  // deixa o tipo de OS legível sem depender do ícone (pedido do Vittorio, 22/07).
  const abrevOs = v.os_tipo ? ABREV_OS[v.os_tipo] : null;
  const codigoNode = (
    <>
      {v.os_codigo}
      {estilo === 'barra' && abrevOs && (
        <span
          className="fw-semibold ms-1"
          style={{ color: `var(--bs-${corBsDoEvento(v)})` }}
        >
          | {abrevOs}
        </span>
      )}
    </>
  );
  const clienteNode = nomeCliente(v.cliente_nome);
  // Herói do card: o que o olho pega primeiro. Cliente costuma "bater" melhor que o
  // código na leitura do dia (a definir com a Suany). O outro vira a 2ª linha.
  const heroNode = heroi === 'cliente' ? clienteNode : codigoNode;
  const subNode = heroi === 'cliente' ? codigoNode : clienteNode;
  // No estilo "Barra" a cor já diz o tipo (na borda esquerda do card) — o ícone sai
  // da 1ª linha (menos ruído).
  const mostrarIconeTipo = estilo === 'classico' && !!info;

  // OS arquivada (Fatia 5b): card borrado e TRAVADO — nenhuma ação, só Desarquivar.
  if (v.os_arquivada) {
    return (
      <div className="w-100 overflow-hidden">
        <div style={{ filter: 'grayscale(1)', opacity: 0.55 }}>
          <div className="d-flex align-items-center gap-1">
            <i className="bi bi-lock-fill small"></i>
            <span className="small fw-medium text-truncated">{heroNode}</span>
          </div>
          <p className="mb-0 text-truncated small">{subNode}</p>
        </div>
        <div className="d-flex justify-content-center mt-2 mb-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-theme btn-desarquivar rounded-pill py-0 px-3"
            onClick={(e) => {
              e.stopPropagation();
              onDesarquivar();
            }}
            title="OS arquivada — desarquivar pra voltar a mexer"
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i> Desarquivar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-100 overflow-hidden ${cancelada ? 'opacity-50' : ''}`}>
      {/* 1ª linha: HERÓI (código ou cliente) [+ ícone do tipo no clássico] · hora à direita */}
      <div className="row gx-1 flex-nowrap align-items-center">
        <div className="col text-truncated">
          <span
            className={`small fw-medium ${cancelada ? 'text-decoration-line-through' : ''}`}
          >
            {heroNode}
          </span>
          {mostrarIconeTipo && (
            <i className={`bi ${info!.icon} evento-acao ms-1`}></i>
          )}
        </div>
        <div className="col-auto ms-auto">
          {/* Relógio à esquerda da hora. Sem setinha. */}
          <button
            type="button"
            onClick={(e) => {
              parar(e);
              onEditarHora();
            }}
            className="evento-acao evento-botao d-inline-flex align-items-center gap-1"
            title={v.hora ? 'Reagendar / mudar hora' : 'Marcar hora / reagendar'}
          >
            <i className="bi bi-clock"></i>
            {v.hora && <span className="fw-bold">{v.hora}</span>}
          </button>
        </div>
      </div>

      {/* 2ª linha: o outro dado (cliente ou código) */}
      <p
        className={`mb-0 text-truncated ${cancelada ? 'text-decoration-line-through' : ''}`}
      >
        {subNode}
      </p>

      {/* 3ª linha: para onde se vai */}
      {/* Linha do endereço: bairro/cidade à esquerda. Quando NÃO há viagem, os
          marcadores (urgência/resultado) moram aqui, à direita (quebram p/ baixo
          se o card for estreito). Com viagem, eles vão na linha da viagem. */}
      {(v.bairro_cidade || !hasViagem) && (
        <div className="d-flex align-items-center gap-1">
          {v.bairro_cidade && (
            <span
              className="opacity-75 small text-truncated"
              style={{ minWidth: 0 }}
            >
              {v.bairro_cidade}
            </span>
          )}
          {!hasViagem && (
            <span className="ms-auto flex-shrink-0">
              <MarcadoresVisita
                v={v}
                onToggleUrgencia={onToggleUrgencia}
                onDefinirResultado={onDefinirResultado}
              />
            </span>
          )}
        </div>
      )}

      {/* Expandido: "quem vai" EDITÁVEL (chips com auto-save — marca/desmarca e grava
          na hora, sem abrir modal nem ir na OS) + a observação. */}
      {expandido && (
        <div className="mt-1 small">
          <div className="d-flex flex-wrap gap-1 mb-1" onClick={parar}>
            {instaladoresOpcoes.length === 0 && (
              <span className="opacity-50">Nenhum instalador cadastrado.</span>
            )}
            {instaladoresOpcoes.map((inst) => {
              const on = v.instaladores.some((x) => x.id === inst.id);
              return (
                <button
                  key={inst.id}
                  type="button"
                  className={`btn btn-sm py-0 px-1 ${on ? 'btn-theme' : 'btn-outline-theme'}`}
                  style={{ fontSize: 11 }}
                  onClick={(e) => {
                    parar(e);
                    onToggleInstalador(inst);
                  }}
                >
                  {on && <i className="bi bi-check"></i>}
                  {inst.nome}
                </button>
              );
            })}
          </div>
          {v.pauta && (
            <p className="mb-0 text-truncated opacity-75">{v.pauta}</p>
          )}
        </div>
      )}

      {/* Linha da viagem: chip liga/desliga + os marcadores à direita (mesma linha). */}
      {hasViagem && (
        <div className="mt-1 d-flex align-items-center gap-1">
          <ChipViagem
            naViagem={naViagem}
            destino={viagemNome}
            onToggle={onToggleViagem}
          />
          <span className="ms-auto flex-shrink-0">
            <MarcadoresVisita
              v={v}
              onToggleUrgencia={onToggleUrgencia}
              onDefinirResultado={onDefinirResultado}
            />
          </span>
        </div>
      )}
      {/* Rodapé: avatares das pessoas à esquerda · localizar/expandir/duplicar/excluir à direita. */}
      <div className="d-flex align-items-center gap-2 mt-1 pt-1 border-top border-secondary border-opacity-25">
        <AvatarGrupo pessoas={v.instaladores} />
        {/* MOSTRAR ESTE CARD — ao lado de quem vai, porque a pergunta é a
            mesma: "quem enxerga esta ida?". Dois estados, e nada manda acima
            dele: marcar o dia e depois tirar um card daqui é exatamente o que
            ela faz ("marca um dia e tira um compromisso"). */}
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onToggleLiberacao();
          }}
          className={`evento-acao evento-botao${v.liberado ? ' text-theme-1' : ''}`}
          title={
            v.liberado
              ? 'O instalador vê este card — clique p/ esconder'
              : 'Mostrar este card para o instalador'
          }
        >
          <i className={`bi ${v.liberado ? 'bi-eye-fill' : 'bi-eye-slash'}`}></i>
        </button>
        <div className="d-flex align-items-center gap-2 ms-auto">
        {/* Pin: acende TODAS as visitas desta OS (pisca) e destaca o cartão na faixa.
            Preenchido quando a OS já está em foco — o mesmo clique também desliga. */}
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onFocarOs();
          }}
          className="evento-acao evento-botao"
          title={
            emFoco
              ? 'Parar de destacar esta OS'
              : 'Mostrar os outros agendamentos desta OS'
          }
        >
          <i className={`bi ${emFoco ? 'bi-geo-alt-fill' : 'bi-geo-alt'}`}></i>
        </button>
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onToggleExpandir();
          }}
          className="evento-acao evento-botao"
          title={expandido ? 'Recolher' : 'Mais linhas'}
        >
          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </button>
        {/* Lápis: o card do evento avulso sempre teve, o da OS não — e a
            assimetria não se explicava (Vittorio, 31/07). Abre a mesma edição
            que o clique na hora já abria. */}
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onEditarHora();
          }}
          className="evento-acao evento-botao"
          title="Editar esta visita"
        >
          <i className="bi bi-pencil"></i>
        </button>
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onDuplicar();
          }}
          className="evento-acao evento-botao"
          title="Duplicar visita"
        >
          <i className="bi bi-copy"></i>
        </button>
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onExcluir();
          }}
          className="evento-acao evento-botao"
          title="Excluir visita"
        >
          <i className="bi bi-trash"></i>
        </button>
        </div>
      </div>
    </div>
  );
}

/** Iniciais pro fallback do avatar (pessoa sem foto). */
function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

/**
 * Avatares sobrepostos das pessoas envolvidas — padrão `avatar-group` do tema
 * (o :not(:first-child) recua com margin negativa, empilhando as bolinhas). Foto
 * quando existe; iniciais como fallback. A borda branca separa uma da outra.
 */
function AvatarGrupo({ pessoas }: { pessoas: Instalador[] }) {
  if (!pessoas.length) return null;
  return (
    <div className="avatar-group d-flex align-items-center">
      {pessoas.map((p) =>
        p.foto ? (
          <span
            key={p.id}
            className="avatar avatar-20 rounded-circle border border-white"
            title={p.nome}
          >
            {/* img direto (sem `coverimg`, que esconde o img e usa background) —
                object-fit cobre o círculo; o .avatar já tem overflow:hidden. */}
            <img
              src={p.foto}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </span>
        ) : (
          <span
            key={p.id}
            className="avatar avatar-20 rounded-circle border border-white bg-theme-1-subtle text-theme-1 d-flex align-items-center justify-content-center"
            title={p.nome}
            style={{ fontSize: 9 }}
          >
            {iniciais(p.nome)}
          </span>
        ),
      )}
    </div>
  );
}

/**
 * Card de um EVENTO avulso (sem OS). Fundo amarelo do tema (`bg-yellow-subtle`),
 * pra distinguir de um agendamento de OS na hora. Mostra só o título (pin + texto)
 * e a hora; o rodapé tem a mesma "linha" de ações da visita de OS (editar/excluir)
 * — não tem OS pra focar/duplicar. Não confundir com `servico_eventos` do backend
 * (timeline de atividades da OS), que é outra coisa.
 */
function EventoAvulso({
  v,
  estilo,
  viagensDoDia,
  naViagem,
  viagemNome,
  onToggleViagem,
  onToggleLiberacao,
  onEditar,
  onExcluir,
}: {
  v: VisitaAgenda;
  estilo: 'classico' | 'barra';
  viagensDoDia: ViagemLite[];
  naViagem: boolean;
  viagemNome: string | null;
  onToggleViagem: (e: ReactMouseEvent) => void;
  onToggleLiberacao: () => void;
  onEditar: () => void;
  onExcluir: () => void;
}) {
  const cancelada = v.status === 'cancelada';
  const parar = (e: ReactMouseEvent) => e.stopPropagation();
  const info =
    TIPO_EVENTO[(v.tipo ?? 'lembrete') as Exclude<TipoEvento, 'os'>] ??
    TIPO_EVENTO.lembrete;
  // No estilo "Barra" a cor já diz o tipo (na borda esquerda do card) — o ícone sai
  // da 1ª linha.
  const mostrarIconeTipo = estilo === 'classico';
  return (
    <div className={`w-100 overflow-hidden ${cancelada ? 'opacity-50' : ''}`}>
      {/* 1ª linha: [ícone do tipo no clássico] título · custo/hora à direita */}
      <div className="row gx-1 flex-nowrap align-items-start">
        <div className="col text-truncated">
          {mostrarIconeTipo && (
            <i className={`bi ${info.icon} evento-acao me-1`} title={info.label}></i>
          )}
          <span
            className={`fw-medium ${cancelada ? 'text-decoration-line-through' : ''}`}
          >
            {v.titulo || info.label}
          </span>
          {/* "$" discreto quando o evento gera custo — lembrete de que há gasto a lançar. */}
          {v.custo === 'sim' && (
            <i
              className="bi bi-cash-coin evento-acao ms-1"
              title="Gera custo"
            ></i>
          )}
        </div>
        {v.hora && (
          <div className="col-auto">
            <span className="evento-acao fw-bold d-inline-flex align-items-center gap-1">
              <i className="bi bi-clock"></i>
              {v.hora}
            </span>
          </div>
        )}
      </div>
      {/* 2ª linha: observação — dá corpo ao card (só com o título ficava pequeno
          demais). Sem observação, um "…" esmaecido segura a linha. */}
      <p
        className={`mb-0 text-truncated small ${v.pauta ? 'opacity-75' : 'opacity-25'}`}
      >
        {v.pauta || '…'}
      </p>
      {/* 3ª linha: para onde se vai — mesma linha (e mesmo formato) do card de
          OS. O evento avulso ganhou endereço próprio na migration 043, e sem
          isto não dava pra ver o destino ao montar o dia. */}
      {v.bairro_cidade && (
        <div className="d-flex align-items-center gap-1">
          <span
            className="opacity-75 small text-truncated"
            style={{ minWidth: 0 }}
          >
            {v.bairro_cidade}
          </span>
        </div>
      )}
      {/* Linha da viagem: chip liga/desliga em linha própria (legível, sem espremer). */}
      {viagensDoDia.length > 0 && (
        <div className="mt-1 d-flex">
          <ChipViagem
            naViagem={naViagem}
            destino={viagemNome}
            onToggle={onToggleViagem}
          />
        </div>
      )}
      {/* Rodapé: avatares das pessoas à esquerda · editar/excluir à direita. */}
      <div className="d-flex align-items-center gap-2 mt-1 pt-1 border-top border-secondary border-opacity-25">
        <AvatarGrupo pessoas={v.instaladores} />
        {/* MOSTRAR ESTE CARD — ao lado de quem vai, porque a pergunta é a
            mesma: "quem enxerga esta ida?". Dois estados, e nada manda acima
            dele: marcar o dia e depois tirar um card daqui é exatamente o que
            ela faz ("marca um dia e tira um compromisso"). */}
        <button
          type="button"
          onClick={(e) => {
            parar(e);
            onToggleLiberacao();
          }}
          className={`evento-acao evento-botao${v.liberado ? ' text-theme-1' : ''}`}
          title={
            v.liberado
              ? 'O instalador vê este card — clique p/ esconder'
              : 'Mostrar este card para o instalador'
          }
        >
          <i className={`bi ${v.liberado ? 'bi-eye-fill' : 'bi-eye-slash'}`}></i>
        </button>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <button
            type="button"
            onClick={(e) => {
              parar(e);
              onEditar();
            }}
            className="evento-acao evento-botao"
            title="Editar evento"
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            type="button"
            onClick={(e) => {
              parar(e);
              onExcluir();
            }}
            className="evento-acao evento-botao"
            title="Excluir evento"
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Seletor de tipo do evento avulso — botões com ícone+cor do `TIPO_EVENTO`. */
function SeletorTipoEvento({
  valor,
  onSelecionar,
}: {
  valor: Exclude<TipoEvento, 'os'>;
  onSelecionar: (t: Exclude<TipoEvento, 'os'>) => void;
}) {
  return (
    <div className="d-flex flex-wrap gap-2">
      {TIPOS_EVENTO.map((t) => {
        const info = TIPO_EVENTO[t];
        const on = valor === t;
        return (
          <button
            key={t}
            type="button"
            className={`btn btn-sm ${on ? 'btn-theme' : 'btn-outline-theme'}`}
            onClick={() => onSelecionar(t)}
          >
            <i className={`bi ${info.icon} me-1`}></i>
            {info.label}
          </button>
        );
      })}
    </div>
  );
}

function ChipsInstaladores({
  opcoes,
  selecionados,
  onToggle,
}: {
  opcoes: Instalador[];
  selecionados: number[];
  onToggle: (id: number) => void;
}) {
  if (opcoes.length === 0)
    return <span className="small text-secondary">Nenhum instalador cadastrado.</span>;
  return (
    <div className="d-flex flex-wrap gap-2">
      {opcoes.map((inst) => {
        const on = selecionados.includes(inst.id);
        return (
          <button
            key={inst.id}
            type="button"
            className={`btn btn-sm ${on ? 'btn-theme' : 'btn-outline-theme'}`}
            onClick={() => onToggle(inst.id)}
          >
            {on && <i className="bi bi-check me-1"></i>}
            {inst.nome}
          </button>
        );
      })}
    </div>
  );
}

type AcaoSpeed = { icon: string; label: string; onClick: () => void };

/**
 * Speed dial (leque de ações que "explode" do ponto do clique). Feito do zero no
 * estilo do tema — bootstrap-icons + classes `theme-*`, sem Material UI. Renderiza
 * num portal (por cima do overflow do calendário) e fecha ao clicar fora.
 */
function SpeedDial({
  left,
  top,
  bottom,
  acoes,
  onClose,
  container,
}: {
  left: number;
  top: number;
  bottom: number;
  acoes: AcaoSpeed[];
  onClose: () => void;
  container?: Element | null;
}) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Abre pra baixo por padrão; se não couber na tela, abre pra cima (flip).
  const estimativa = acoes.length * 46 + 8;
  const abreAcima = bottom + estimativa > window.innerHeight - 12;
  const pos = abreAcima
    ? { left, bottom: window.innerHeight - top + 6 }
    : { left, top: bottom + 6 };

  return createPortal(
    <div className="sd-backdrop" onClick={onClose}>
      <div
        className={`sd-dial${abreAcima ? ' sd-up' : ''}`}
        style={pos}
        onClick={(e) => e.stopPropagation()}
      >
        {acoes.map((a, i) => (
          <button
            key={i}
            type="button"
            className={`sd-item${on ? ' sd-on' : ''}`}
            style={{ transitionDelay: `${i * 45}ms` }}
            onClick={() => {
              onClose();
              a.onClick();
            }}
          >
            <span className="sd-ic bg-theme-1 text-white">
              <i className={`bi ${a.icon}`}></i>
            </span>
            <span className="sd-lb bg-body text-body">{a.label}</span>
          </button>
        ))}
      </div>
    </div>,
    container ?? document.body,
  );
}

/** Estado da visita → classe de badge do Bootstrap (o tema já pinta as `text-bg-*`). */
const BADGE_VISITA: Record<OSVisita['status'], string> = {
  agendada: 'text-bg-secondary',
  realizada: 'text-bg-success',
  nao_realizada: 'text-bg-warning',
  cancelada: 'text-bg-danger',
};

/**
 * Modal do evento da agenda — o MESMO para visita de OS e para evento avulso.
 *
 * Nasceu de duas telas que falavam do mesmo assunto com pesos diferentes
 * (Vittorio, 31/07): uma larga e chapada, outra estreita e vazia; em nenhuma
 * das duas o olho tinha onde pousar — cliente, status e "Notas fiscais (0)"
 * pesavam igual — e de nenhuma delas dava pra excluir (foi o que travou a Suany
 * quando o dia lotou). Aqui o esqueleto é único:
 *
 *   cabeçalho (ícone do tipo + tipo/código + título)  ·  abas  ·  rodapé
 *
 * e o que muda é o recheio: a OS traz produtos/NF/idas, o avulso não.
 *
 * Regra de destaque (a que faltava): **um destaque por bloco**. Peso fica com o
 * título, com a DATA e com o badge de estado; todo o resto é `text-secondary`.
 * Contraste demais é o mesmo que contraste nenhum.
 */
function ModalEvento({
  carregando,
  os,
  visita,
  container,
  viagemNome,
  pautaEdit,
  setPautaEdit,
  salvandoPauta,
  onSalvarPauta,
  onFechar,
  onAbrirOs,
  onEditar,
  onExcluir,
}: {
  carregando: boolean;
  os: OSDetalhe | null;
  visita: VisitaAgenda | null;
  container?: HTMLElement | null;
  viagemNome: string | null;
  pautaEdit: string | null;
  setPautaEdit: (v: string | null) => void;
  salvandoPauta: boolean;
  onSalvarPauta: () => void;
  onFechar: () => void;
  onAbrirOs: () => void;
  onEditar: () => void;
  onExcluir: () => void;
}) {
  const [aba, setAba] = useState('visita');
  // Todo evento abre pela primeira aba — senão o próximo herda a aba do anterior
  // e a pessoa cai em "Notas fiscais" sem ter pedido.
  useEffect(() => {
    setAba('visita');
  }, [visita?.id]);

  const infoAvulso = infoEvento(visita?.tipo);
  const corAvulso =
    COR_BS_EVENTO[
      (visita?.tipo && visita.tipo !== 'os' ? visita.tipo : 'lembrete') as Exclude<
        TipoEvento,
        'os'
      >
    ] ?? 'yellow';

  /* Ícone do tipo no padrão canônico do tema (avatar redondo com fundo suave).
     A cor sai do TIPO: na OS, aplicando a paleta dele no próprio elemento
     (`theme-*` vale em qualquer tag) — assim `bg-theme-1-subtle` já nasce na cor
     certa, sem hex; no avulso, pela matiz da categoria. */
  const avatar =
    os && os.tipo
      ? `avatar avatar-40 rounded-circle bi ${TIPO_INFO[os.tipo].icon} bg-theme-1-subtle text-theme-1 ${TIPO_INFO[os.tipo].tema}`
      : `avatar avatar-40 rounded-circle bi ${infoAvulso.icon} bg-${corAvulso}-subtle text-${corAvulso}`;

  const endereco = os
    ? [
        [os.endereco, os.numero].filter(Boolean).join(', '),
        os.bairro,
        os.cidade,
      ]
        .filter(Boolean)
        .join(' · ')
    : (visita?.endereco_resumo ?? '');

  /** Bloco "O que fazer nesta ida" — igual nos dois mundos, é o que mais se mexe. */
  const pauta = visita && (
    <div className="rounded border p-3">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
        <span className="fw-medium d-inline-flex align-items-center gap-2">
          <i className="bi bi-card-checklist text-secondary"></i>O que fazer
          {visita.data ? ` em ${formatarDiaMes(visita.data)}` : ''}
        </span>
        {pautaEdit === null ? (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-decoration-none"
            onClick={() => setPautaEdit(visita.pauta ?? '')}
          >
            <i className="bi bi-pencil me-1"></i>
            {visita.pauta ? 'Editar' : 'Escrever'}
          </button>
        ) : (
          <span className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-decoration-none text-secondary"
              onClick={() => setPautaEdit(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-decoration-none"
              onClick={onSalvarPauta}
              disabled={salvandoPauta}
            >
              {salvandoPauta && <span className="spinner-border spinner-border-sm me-1" />}
              Salvar
            </button>
          </span>
        )}
      </div>
      {/* ms-4 = a largura do ícone + o gap: o texto começa embaixo do TÍTULO, e
          não encostado na borda (o desalinho que o Vittorio apontou). */}
      {pautaEdit === null ? (
        <p className={`mb-0 ms-4 ${visita.pauta ? '' : 'text-secondary fst-italic'}`}>
          {visita.pauta || 'Sem nada escrito para esta ida.'}
        </p>
      ) : (
        <div className="ms-4">
          <input
            className="form-control form-control-sm"
            autoFocus
            placeholder="Ex.: finalizar o quarto da empregada"
            value={pautaEdit}
            onChange={(e) => setPautaEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSalvarPauta();
              if (e.key === 'Escape') setPautaEdit(null);
            }}
          />
          <span className="aviso-roteiro mt-1">
            <i className="bi bi-printer"></i> Sai no roteiro impresso desta data
          </span>
        </div>
      )}
    </div>
  );

  /** Aba 1 — a ida em si: quando, em que estado, onde, com quem, pra quê. */
  const abaVisita = visita && (
    <div className="d-flex flex-column gap-3">
      {/* A DATA é o dado principal de um evento de agenda: ganha peso de texto,
          não fica em legenda cinza como estava. */}
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="d-inline-flex align-items-center gap-2 fs-14 fw-medium">
          <i className="bi bi-calendar-event text-secondary"></i>
          {visita.data ? formatarDiaMes(visita.data) : 'Sem data'}
          {visita.hora ? ` · ${visita.hora}` : ''}
        </span>
        <span className={`badge ${BADGE_VISITA[visita.status]}`}>
          {rotuloStatusVisita(visita.status)}
        </span>
        {viagemNome && (
          <span className="badge rounded-pill bg-theme-1">
            <i className="bi bi-signpost-2 me-1"></i>
            {viagemNome}
          </span>
        )}
      </div>

      {/* Metadados: todos na mesma coluna de ícone, pra texto alinhar com texto. */}
      <div className="d-flex flex-column gap-1 small text-secondary">
        {os && (
          <span className="linha-meta">
            <i className="bi bi-tag"></i>
            <span>
              OS {rotuloStatus(os.status)}
              {os.pedido_venda ? ` · Pedido ${os.pedido_venda}` : ''}
            </span>
          </span>
        )}
        {endereco && (
          <span className="linha-meta">
            <i className="bi bi-geo-alt"></i>
            <span>{endereco}</span>
          </span>
        )}
        {visita.instaladores.length > 0 && (
          <span className="linha-meta">
            <i className="bi bi-people"></i>
            <span>{visita.instaladores.map((i) => i.nome).join(' · ')}</span>
          </span>
        )}
        {visita.os_ref && (
          <span className="linha-meta">
            <i className="bi bi-link-45deg"></i>
            <span>OS a vincular: {visita.os_ref}</span>
          </span>
        )}
      </div>

      {pauta}
    </div>
  );

  /** Lista simples com estado vazio alinhado — o mesmo desenho nas três abas. */
  const lista = (vazio: string, itens: ReactNode[]) =>
    itens.length === 0 ? (
      <p className="small text-secondary mb-0">{vazio}</p>
    ) : (
      <ul className="list-group list-group-flush">{itens}</ul>
    );

  return (
    <Modal
      show={carregando || !!visita}
      onHide={onFechar}
      centered
      size="lg"
      dialogClassName="modal-evento"
      container={container ?? undefined}
    >
      {carregando && !os ? (
        <Modal.Body className="text-center py-5 text-secondary">
          <span className="spinner-border spinner-border-sm me-2" /> Carregando…
        </Modal.Body>
      ) : visita ? (
        <>
          <Modal.Header closeButton className="align-items-center gap-3">
            <i className={avatar}></i>
            <div className="me-auto">
              {/* Linha de cima = a que identifica (código e tipo); embaixo, o
                  nome, que é o que a pessoa procura na tela. */}
              <p className="small text-secondary mb-0">
                {os ? `${os.codigo} · ${rotuloTipo(os.tipo)}` : infoAvulso.label}
              </p>
              <Modal.Title className="h5 mb-0">
                {os ? nomeCliente(os.cliente_nome) : visita.titulo || 'Evento'}
              </Modal.Title>
            </div>
          </Modal.Header>

          <Modal.Body>
            {os ? (
              <Tabs
                className="adminuiux-tabs mb-3"
                activeKey={aba}
                onSelect={(k) => setAba(k || 'visita')}
              >
                <Tab eventKey="visita" title="Visita">
                  {abaVisita}
                </Tab>
                <Tab eventKey="produtos" title={`Produtos (${os.itens.length})`}>
                  {lista(
                    'Nenhum item na OS.',
                    os.itens.map((it, i) => (
                      <li
                        key={i}
                        className="list-group-item px-0 py-1 d-flex justify-content-between gap-2 small bg-transparent"
                      >
                        <span className="text-truncate">{it.descricao}</span>
                        <span className="text-secondary flex-shrink-0">
                          {it.quantidade}x
                        </span>
                      </li>
                    )),
                  )}
                </Tab>
                <Tab
                  eventKey="levar"
                  title={`Levar & NF (${os.recursos.length + os.notas.length})`}
                >
                  <p className="small fw-medium mb-1">
                    <i className="bi bi-tools me-2 text-secondary"></i>O que levar
                  </p>
                  {lista(
                    'Nada marcado na aba Roteiro da OS.',
                    os.recursos.map((r, i) => (
                      <li
                        key={i}
                        className="list-group-item px-0 py-1 small bg-transparent d-flex justify-content-between gap-2"
                      >
                        <span className="text-truncate">
                          {r.nome}
                          {r.observacao ? (
                            <span className="text-secondary"> — {r.observacao}</span>
                          ) : null}
                        </span>
                        {r.quantidade != null && (
                          <span className="text-secondary flex-shrink-0">
                            {r.quantidade}x
                          </span>
                        )}
                      </li>
                    )),
                  )}
                  <p className="small fw-medium mb-1 mt-3">
                    <i className="bi bi-receipt me-2 text-secondary"></i>Notas fiscais
                  </p>
                  {lista(
                    'Nenhuma nota lançada.',
                    os.notas.map((n, i) => (
                      <li
                        key={i}
                        className="list-group-item px-0 py-1 small bg-transparent"
                      >
                        {[
                          n.fornecedor,
                          n.numero ? `NF ${n.numero}` : null,
                          n.volumes != null ? `${n.volumes} vol.` : null,
                          n.conteudo,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </li>
                    )),
                  )}
                </Tab>
                <Tab eventKey="idas" title={`Idas (${os.visitas.length})`}>
                  {/* O histórico das idas: mesma OS, dias diferentes, propósitos
                      diferentes ("trilhos" na sexta, "finalizar a suíte" na segunda). */}
                  {lista(
                    'Nenhuma visita marcada.',
                    os.visitas.map((vi) => (
                      <li
                        key={vi.id}
                        className={`list-group-item px-0 py-1 small bg-transparent d-flex gap-2 ${
                          vi.id === visita.id ? 'fw-medium' : ''
                        }`}
                      >
                        <span className="text-secondary flex-shrink-0">
                          {vi.data ? formatarDiaMes(vi.data) : '—'}
                          {vi.hora ? ` ${vi.hora}` : ''}
                        </span>
                        <span className="text-truncate">
                          {(vi.id === visita.id ? visita.pauta : vi.pauta) || '—'}
                        </span>
                      </li>
                    )),
                  )}
                </Tab>
              </Tabs>
            ) : (
              /* Avulso não ganha barra de abas: teria uma aba só, o que é pior
                 que não ter. O esqueleto (cabeçalho, corpo, rodapé) é o mesmo. */
              abaVisita
            )}
          </Modal.Body>

          <Modal.Footer className="justify-content-between">
            {/* Excluir mora AQUI, no modal — antes só existia na lixeirinha do card,
                que sumia da tela quando o dia enchia. Link discreto: o peso do ato
                vem do modal de confirmação, não de um botão vermelho. */}
            <button
              type="button"
              className="btn btn-link theme-red text-decoration-none px-0"
              onClick={onExcluir}
            >
              <i className="bi bi-trash me-1"></i>Excluir
            </button>
            <span className="d-flex align-items-center gap-2">
              <button type="button" className="btn btn-link" onClick={onFechar}>
                Fechar
              </button>
              <button
                type="button"
                className={os ? 'btn btn-outline-theme' : 'btn btn-theme'}
                onClick={onEditar}
              >
                <i className="bi bi-pencil-square me-1"></i>
                {os ? 'Editar visita' : 'Editar tudo'}
              </button>
              {os && (
                <button type="button" className="btn btn-theme" onClick={onAbrirOs}>
                  <i className="bi bi-box-arrow-up-right me-1"></i> Abrir OS
                </button>
              )}
            </span>
          </Modal.Footer>
        </>
      ) : null}
    </Modal>
  );
}
